import axios from "axios";
import {
  getTokenFromLocalStorage,
  persistRefreshedSession,
  sessionExpired,
} from "../utilities/tokenUtils.js";
import { getActiveCountry } from "../utilities/activeCountry.js";
import { adminSessionToken } from "../utilities/impersonation.js";

/**
 * Dashboard SPA must call the Node API, not the webpack dev server.
 * If REACT_APP_SERVER_URL is unset, CRA would POST to localhost:3000 → "User doesn't exist".
 * In development, default to the usual local API port (override with frontend/.env).
 */
export function dashboardApiBaseUrl() {
  const raw = process.env.REACT_APP_SERVER_URL;
  if (raw != null && String(raw).trim() !== "") {
    return String(raw).trim().replace(/\/$/, "");
  }
  if (process.env.NODE_ENV === "development") {
    return "http://localhost:8001";
  }
  return "";
}

/**
 * The configured client. EVERY authenticated dashboard call must go through
 * this — not bare `axios`.
 *
 * It carries two interceptors that are easy to lose by accident:
 *   • REQUEST — attaches the Bearer token and the active-country header.
 *   • RESPONSE — silently refreshes an expired token, retries the call, and
 *     falls back to `sessionExpired()` (clear storage → toast → LOGOUT →
 *     redirect to login) when the refresh itself fails.
 *
 * A bare `axios.post(...)` gets NEITHER. It sends no token, so the server
 * answers 401 NO_TOKEN even though the user is perfectly well signed in, and
 * the 401 never reaches the session machinery — so nothing refreshes, nothing
 * redirects, and the failure surfaces as an unexplained toast. That is exactly
 * how "Missing Authorization Token" appeared on the category form for a signed-
 * in admin. `__tests__/noBareAxiosInActions.test.js` guards against it coming
 * back.
 */
/* A DEFAULT TIMEOUT IS NOT OPTIONAL. axios defaults to 0, meaning "wait
   forever", so any stalled request left its screen spinning with nothing in the
   console to explain it — the dashboard's version of the bug that made the
   mobile apps hang (see the apiClient timeout note in the Expo client).
   "Loading brands…" and "Loading products…" both hung on a stopped API server.

   60s is deliberately generous: it is far longer than any REST call here
   legitimately takes, so it only ever fires on something genuinely wedged.
   Cloudinary uploads do not travel through this instance. A call that really
   does need longer should pass its own `timeout` rather than this being
   loosened for everything. */
const API = axios.create({
  baseURL: dashboardApiBaseUrl(),
  withCredentials: true,
  timeout: 60000,
});

// ── Attach access token + active country to every request ───────────────────────────
API.interceptors.request.use((req) => {
  // Read the access token directly. getUserFromLocalStorage intentionally
  // rejects expired tokens, which would end the session before the 401
  // response interceptor gets a chance to exchange the refresh token.
  //
  // `adminSession: true` on a request config sends it with the super admin's
  // OWN token while they are signed in as a vendor (utilities/impersonation.js
  // adminSessionToken). Outside a vendor session there is no stash and the
  // normal token, which is the admin's, is used.
  const token =
    (req.adminSession && adminSessionToken()) || getTokenFromLocalStorage();
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  // Stamp the active market so the backend can scope reads/writes by country.
  // Advisory only — the server (enforceStaffCountry) is the real boundary and
  // ignores/403s a country a non-super identity is not permitted to see.
  req.headers["x-country"] = getActiveCountry();
  return req;
});

// ── Silent refresh on 401 ─────────────────────────────────────────────────────────
let isRefreshingDashboard = false;

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const code = error.response?.data?.code;

    if (
      error.response?.status === 401 &&
      (code === "TOKEN_EXPIRED" || code === "INVALID_TOKEN" || code === "NO_TOKEN") &&
      !originalRequest._retry &&
      // An admin-session call made from inside a vendor session must never
      // trigger a refresh: the refresh token in storage is the VENDOR's (none),
      // and failing it would sign the admin out of the vendor view they are in.
      // The caller tells the admin their own session has lapsed instead.
      !originalRequest.adminSession
    ) {
      if (isRefreshingDashboard) {
        // Another refresh is in progress — just reject this one
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshingDashboard = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) throw new Error("No refresh token stored");

        const { data } = await axios.post(
          `${dashboardApiBaseUrl()}/user/refresh-token`,
          { refreshToken },
          // Raw axios bypasses the request interceptor — stamp x-country here
          // too so the refresh call carries the active market like every other.
          { headers: { "x-country": getActiveCountry() } }
        );

        // Keep the encrypted profile, standalone access token, and rotated
        // refresh token in sync.
        persistRefreshedSession(data);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        // Refresh token invalid/expired — force re-login with user-friendly feedback
        console.error("[Dashboard] Refresh token expired. Forcing logout.");
        sessionExpired('forced');
        return Promise.reject(refreshError);
      } finally {
        isRefreshingDashboard = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── Sign in as vendor (super user only) ──────────────────────────────────────
// Start takes the vendor's OWNER login id (any vertical) and, optionally, the
// storefront clicked; returns { result, token, impersonation }, which
// utilities/impersonation.js swaps in for the admin session. End closes it server-side ("Return to
// admin"); called with the VENDOR token still in place.
export const apiStartVendorImpersonation = (ownerId, vendorId) =>
  API.post(`/user/impersonate/vendor/${ownerId}`, vendorId ? { vendorId } : {});
export const apiEndVendorImpersonation = () => API.post(`/user/impersonate/end`);

export const createOrderEndPoint = (newCODOrder) =>
  API.post(`/weborders`, newCODOrder);

// In-house / phone-in order placement
export const placeInHouseOrderApi = (orderData) =>
  API.post(`/weborders/inhouse`, orderData);

// ── Web Push (dashboard operator alerts) ───────────────────────────────────────
// All requests go through the `API` instance, so they carry the auth token and
// the active `x-country` header automatically (country scoping stays consistent).
export const apiGetVapidPublicKey = () => API.get(`/webpush/vapid-public-key`);
export const apiSubscribeWebPush = (payload) => API.post(`/webpush/subscribe`, payload);
export const apiUnsubscribeWebPush = (endpoint) => API.post(`/webpush/unsubscribe`, { endpoint });
export const apiSendTestWebPush = () => API.post(`/webpush/test`, {});
export const apiListPushDevices = () => API.get(`/webpush/devices`);
export const apiGetNotificationSettings = () => API.get(`/webpush/settings`);
export const apiUpdateNotificationSettings = (settings) => API.put(`/webpush/settings`, settings);
export const apiGetNotificationHistory = (params = {}) => API.get(`/webpush/history`, { params });
export const apiBroadcastWebPush = (payload) => API.post(`/webpush/broadcast`, payload);
// Expo/mobile broadcast (customer app + riders) — routed through API so it uses
// the correct base URL in dev AND prod (the old raw fetch was hard-coded to prod).
// The whole payload goes through untouched, and it is the caller's whole form:
// { title, message, audience, userId?, country?, data?, respectPrefs }. Narrowing
// it here would silently drop the audience and reach every market, so do not.
// Build it with utilities/pushBroadcast.js buildPushBroadcastBody().
export const apiSendExpoBroadcast = (payload) => API.post(`/expoPushTokens/message`, payload);
// Scheduled and recent push campaigns. Every send is recorded, so this is the
// operator's answer to "did that offer go out, and to how many devices".
// `params` is { status?: "scheduled"|"sending"|"completed"|"cancelled"|"failed",
// limit?: number }; omit it for the most recent of everything.
export const apiListPushCampaigns = (params = {}) =>
  API.get(`/expoPushTokens/campaigns`, { params });
// Call back a campaign that has not started. The API answers 409 when it is
// already sending or finished, which the caller must show rather than swallow.
export const apiCancelPushCampaign = (campaignId) =>
  API.post(`/expoPushTokens/campaigns/${campaignId}/cancel`);
// The push doctor, from the server's side: accounts reachable by platform and
// market, the last Expo error per account with what it means, receipt backlog
// and a verdict. `{ receipts: 1 }` also drains due delivery receipts first, so
// a campaign's delivered/failed counts can be read without waiting for the
// five-minute poller. Admin only.
export const apiGetPushHealth = (params = {}) =>
  API.get(`/expoPushTokens/health`, { params });
// Lightweight sound config for the alert engine (any authenticated user).
export const apiGetNotificationClientConfig = () => API.get(`/webpush/client-config`);
// Upload a custom alert tone (base64 data URL) — any operator.
export const apiUploadTone = (dataUrl, name) => API.post(`/webpush/tone`, { dataUrl, name });
// Personal (per-operator) notification preferences — the "My alerts" tab.
export const apiGetMyNotificationPrefs = () => API.get(`/webpush/my-prefs`);
export const apiUpdateMyNotificationPrefs = (prefs) => API.put(`/webpush/my-prefs`, prefs);
export const apiResetMyNotificationPrefs = () => API.put(`/webpush/my-prefs`, { reset: true });

// ── Vendor alert settings (one panel for every vendor type) ───────────────────
// Unified email / SMS / WhatsApp alert prefs for the signed-in vendor. `null`
// in `prefs` means "inherit" (the legacy profile toggle or the default);
// `resolved` is what the dispatcher will actually do.
export const apiGetVendorAlerts = () => API.get(`/api/vendor-alerts/me`);
export const apiUpdateVendorAlerts = (body) => API.put(`/api/vendor-alerts/me`, body);
export const apiSendVendorTestAlert = () => API.post(`/api/vendor-alerts/me/test`, {});

// Create Section
export const apiCreateSection = (sectionData, userId) =>
  API.post(`/restaurants/menu-sections/${userId}`, sectionData);

// Create Restaurant Menu Product Section
export const apiCreateRestaurantProduct = (restaurantProductData, userId) =>
  API.post(`/restaurants/restaurant-products/${userId}`, restaurantProductData);

// Fetch Restaurant product details
export const apiFetchRestaurantProductDetail = (productId) =>
  API.get(`/restaurants/restaurant-product-detail/${productId}`);

// Update Restaurant product
export const apiUpdateRestaurantProduct = (productId, updateData) =>
  API.put(`/restaurants/restaurant-product/${productId}`, updateData);

//Delete restaurant product
export const apiDeleteRestaurantProduct = (productId) =>
  API.delete(`/restaurants/del-restaurant-product/${productId}`);

/* Remove chosen photos (cover and/or extra angles) from ONE product now,
   reclaiming them from Cloudinary, without saving the rest of the form. One
   client function for every catalogue: only the mount differs per type. */
const PRODUCT_IMAGES_PATH = {
  restaurant: (id) => `/restaurants/restaurant-product/${id}/images`,
  healthcare: (id) => `/healthcare/healthcare-product/${id}/images`,
  retailer_wholesaler: (id) => `/rw/product/${id}/images`,
  brand: (id, { brandId } = {}) => `/api/brands/${brandId}/products/${id}/images`,
};
export const apiRemoveProductImages = (productType, productId, urls, opts = {}) => {
  const path = PRODUCT_IMAGES_PATH[productType];
  if (!path) return Promise.reject(new Error(`Unknown product type: ${productType}`));
  return API.delete(path(productId, opts), { data: { urls } });
};

// Delete Restaurant Ssection Along with it's products
export const apiDeleteSection = (sectionId) =>
  API.delete(`/restaurants/del-restaurant-section/${sectionId}`);

// Get Restaurants ||Conditionally get restaurants by vendorId if vendorId is provided
// export const apiFetchRestaurants = () => API.get('/restaurants');
// export const apiFetchRestaurants = (vendorId) => API.get(`/restaurants?vendorId=${vendorId}`);

// Platform staff use the authenticated RBAC directory. Vendor accounts remain
// scoped to the restaurant they own.
export const apiFetchRestaurants = (vendorId, canViewDirectory) =>
  canViewDirectory
    ? API.get("/restaurants/admin-directory")
    : API.get("/restaurants", { params: { vendorId } });

// Update Restaurant
export const apiUpdateRestaurant = (id, updatedData) =>
  API.put(`/restaurants/${id}`, updatedData);

// Delete Restaurant
export const apiDeleteRestaurant = (id) => API.delete(`/restaurants/${id}`);

// Release a restaurant to customers, or pull it back to a private draft while
// its menu is being built. Distinct from isActive, which is suspension.
export const apiSetRestaurantPublishStatus = (id, publish) =>
  API.put(`/restaurants/${id}/publish`, { publish });

/*
 * Vendor order books, stats and sales take an optional `outletId` (4 Oct 2026):
 * "main", a branch id, or several joined by commas. Absent, the server reads
 * every location the login may see (the whole business for head office, the
 * member's branches for a branch member). The navbar's branch picker
 * (context/BranchScopeContext.js outletIdParam) is what the screens pass.
 */
const withOutlet = (params = {}, outletId) => (outletId ? { ...params, outletId } : params);
// The list filters a dashboard card sets (`delivery`, `method`; see
// utilities/orderDeepLinks.js), passed through to the server, which applies
// them in utils/orderListFilters.js so they cut across pages. Empty values
// are left out of the query string.
const withListFilters = (params = {}, { delivery, method } = {}) => ({
  ...params,
  ...(delivery ? { delivery } : {}),
  ...(method ? { method } : {}),
});

// Get Restaurant Vendor Stats
// Daily/weekly/monthly sales overview — generic across vendor types.
// Path is /sales-analytics/:vendorId (avoids the /vendor/:orderId/:vendorId shadow).
export const apiFetchVendorSalesAnalytics = (vendorId, range = "day", { outletId } = {}) =>
  API.get(`/weborders/sales-analytics/${vendorId}`, { params: withOutlet({ range }, outletId) });
// Legacy alias (restaurant route) — kept for back-compat.
export const apiFetchRestaurantSalesAnalytics = (vendorId, range = "day", { outletId } = {}) =>
  API.get(`/weborders/restaurant-vendor/${vendorId}/sales-analytics`, { params: withOutlet({ range }, outletId) });
// Admin "Sales 360" — cross-vendor platform analytics (staff only).
export const apiFetchPlatformSalesAnalytics = (range = "day") =>
  API.get(`/weborders/platform/sales-analytics`, { params: { range } });
/* Money analytics — the SAME order economics for three audiences, scoped by the
   server: the platform P&L (staff only), a 3PL partner's delivery earnings, and a
   vendor's sales + commission. `params` accepts { days } or { from, to }. */
export const apiFetchPlatformMoneyAnalytics = (params = {}) =>
  API.get(`/api/money/platform`, { params });
/* A 3PL login reads itself through /partner; Lusuku staff read any partner
   through /staff/partner, which is gated on the admin token instead. */
export const apiFetchPartnerMoneyAnalytics = (partnerId, params = {}, { asStaff = false } = {}) =>
  API.get(`/api/money/${asStaff ? "staff/partner" : "partner"}/${partnerId}`, { params });
export const apiFetchVendorMoneyAnalytics = (vendorId, params = {}) =>
  API.get(`/api/money/vendor/${vendorId}`, { params });

/* Who bears the payment-rail charge. Readable by any signed-in dashboard user
   (a vendor screen explains the line); writable only by a super admin, enforced
   server-side. */
export const apiFetchPaymentRailPolicy = () => API.get(`/api/payment-rail-policy`);
export const apiUpdatePaymentRailPolicy = (payload) =>
  API.put(`/api/payment-rail-policy`, payload);
export const apiUpdatePaymentRailRates = (payload) =>
  API.put(`/api/payment-rail-policy/rates`, payload);

// HTTP fallback for the admin dashboard stat cards (socket delivers live updates).
export const apiGetDashboardStats = () => API.get(`/weborders/dashboard/stats`);

export const apiFetchRestaurantVendorStat = (vendorId, { outletId } = {}) =>
  API.get(`/weborders/restaurant-vendor/${vendorId}/stats`, { params: withOutlet({}, outletId) });

// Get Seller Stats (Both Retailers & Retailers)
export const apiFetchSellerStats = (vendorId, { outletId } = {}) =>
  API.get(`/weborders/stats-seller/${vendorId}`, { params: withOutlet({}, outletId) });

// Get Brand Vendor Stats (brand orders + open quote requests) — single brand
export const apiFetchBrandStats = (brandId, { outletId } = {}) =>
  API.get(`/api/brands/${brandId}/stats`, { params: withOutlet({}, outletId) });

// Get Brand Owner Stats — aggregates across ALL brands owned by the authenticated user
export const apiFetchBrandOwnerStats = ({ outletId } = {}) =>
  API.get(`/api/brands/owner-stats`, { params: withOutlet({}, outletId) });

export const apiFetchChannelConnections = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/channels`);

export const apiConnectWhatsAppChannel = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/channels/whatsapp`, payload);

export const apiDisconnectWhatsAppChannel = (vendorId, connectionId) =>
  API.delete(`/api/vendor/${vendorId}/channels/whatsapp/${connectionId}`);

// Complete WhatsApp Embedded Signup: vendor authorized their own WABA in the popup.
// payload: { code, phoneNumberId, wabaId, metaBusinessId?, displayPhoneNumber? }
export const apiCompleteWhatsAppEmbeddedSignup = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/channels/whatsapp/embedded-signup`, payload);

export const apiFetchInboxConversations = (vendorId, params = {}) =>
  API.get(`/api/vendor/${vendorId}/inbox/conversations`, { params });

export const apiFetchInboxConversationCounts = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/inbox/counts`);

export const apiFetchChatAnalytics = (vendorId, scope) =>
  API.get(`/api/vendor/${vendorId}/inbox/analytics${scope === "all" ? "?scope=all" : ""}`);

export const apiFetchChatInsights = (vendorId, scope) =>
  API.get(`/api/vendor/${vendorId}/inbox/insights${scope === "all" ? "?scope=all" : ""}`);

// Contacts / address book
export const apiFetchContacts = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/contacts`);
export const apiSaveContact = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/contacts`, payload);
export const apiDeleteContact = (vendorId, contactId) =>
  API.delete(`/api/vendor/${vendorId}/contacts/${contactId}`);

// Dispositions / quick replies
export const apiFetchDispositions = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/dispositions`);
export const apiCreateDisposition = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/dispositions`, payload);
export const apiDeleteDisposition = (vendorId, dispositionId) =>
  API.delete(`/api/vendor/${vendorId}/dispositions/${dispositionId}`);

export const apiFetchInboxConversation = (vendorId, conversationId) =>
  API.get(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}`);

export const apiSendInboxMessage = (vendorId, conversationId, payload) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/messages`, payload);

// Send the vendor's full menu as a WhatsApp interactive list — the customer
// picks items/options/quantities natively and the selection comes back as an
// order draft on the conversation (inbox:order_draft_saved).
export const apiSendInboxMenu = (vendorId, conversationId) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/send-menu`, {});

// Mark a conversation read (decoupled from replying), react to a message, and
// relay agent typing — the "real WhatsApp" interaction set.
export const apiMarkConversationRead = (vendorId, conversationId) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/read`, {});

export const apiReactToMessage = (vendorId, conversationId, messageId, emoji) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/messages/${messageId}/react`, { emoji });

export const apiSendTyping = (vendorId, conversationId) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/typing`, {});

// Agent-started chats: list the vendor's approved templates and start a chat.
export const apiFetchWhatsAppTemplates = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/inbox/templates`);

export const apiStartAgentConversation = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/inbox/start-conversation`, payload);

// Curated starter-template library + one-click submit to the vendor's WABA.
export const apiFetchTemplateLibrary = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/inbox/template-library`);

export const apiSubmitTemplate = (vendorId, templateKey) =>
  API.post(`/api/vendor/${vendorId}/inbox/template-library/submit`, { templateKey });

export const apiAssignInboxConversation = (vendorId, conversationId, payload = {}) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/assign`, payload);

// Persist an order draft without creating the order (status -> order_pending_confirmation).
export const apiSaveInboxOrderDraft = (vendorId, conversationId, payload = {}) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/convert-to-order`, {
    ...payload,
    confirm: false,
  });

// Confirm the draft and create the linked Lusuku order.
export const apiConvertInboxConversationToOrder = (vendorId, conversationId, payload = {}) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/convert-to-order`, {
    ...payload,
    confirm: true,
  });

export const apiCreateInboxTicket = (vendorId, conversationId, payload = {}) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/create-ticket`, payload);

// Move a ticket through its lifecycle: { status: "assigned"|"escalated"|"resolved"|"closed"|"open", notes }
export const apiUpdateInboxTicket = (vendorId, conversationId, ticketId, payload = {}) =>
  API.patch(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/tickets/${ticketId}`, payload);

// Start a fresh order on a thread that already produced one (keeps order history).
export const apiStartNewInboxOrder = (vendorId, conversationId) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/new-order`, {});

// Record payment confirmation (transaction ID) for the conversation's linked order.
export const apiConfirmInboxOrderPayment = (vendorId, conversationId, payload) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/confirm-payment`, payload);

export const apiCloseInboxConversation = (vendorId, conversationId, payload = {}) =>
  API.post(`/api/vendor/${vendorId}/inbox/conversations/${conversationId}/close`, payload);

export const apiFetchVendorInboxCatalog = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/inbox/catalog`);

// Inbox automation: offline WhatsApp alerts + chat distribution strategy.
export const apiFetchInboxAutomationSettings = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/inbox/automation-settings`);

export const apiUpdateInboxAutomationSettings = (vendorId, payload) =>
  API.put(`/api/vendor/${vendorId}/inbox/automation-settings`, payload);

// ── WhatsApp Campaigns (Phase 1) ─────────────────────────────────────────────
// Outbound template campaigns to a tag-based audience, gated on explicit
// marketing opt-in. All endpoints live under /api/vendor/:vendorId and mirror
// the apiFetchInbox* style above.

// Distinct contact tags the vendor can target. → { tags: string[] }
export const apiFetchContactTags = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/contacts/tags`);

// Consent ledger: opted-in / opted-out totals + records.
// → { optedIn, optedOut, records:[{ phone, name, marketingOptIn, optedOut, optInSource, updatedAt }] }
export const apiFetchConsent = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/consent`);

// Record a phone's marketing opt-in / opt-out.
// payload: { phone, marketingOptIn?, optedOut?, source? } → { record }
export const apiSetConsent = (vendorId, payload) =>
  API.post(`/api/vendor/${vendorId}/consent`, payload);

// Live audience preview for an audience definition (Phase 2).
// Accepts either a full audience object — { source, tags?, orderWindowDays?,
// minOrders?, notOrderedSinceDays?, chattedWithinDays? } — or, for backward
// compatibility, a bare array of tags (treated as { source:'tag', tags }).
// → { total, optedIn, optedOut, noConsent, frequencyCapped, sendable, sample:[{phone,name}] }
export const apiPreviewAudience = (vendorId, audience) => {
  const body = Array.isArray(audience)
    ? { audience: { source: "contacts_tag", tags: audience } }
    : { audience };
  return API.post(`/api/vendor/${vendorId}/campaigns/audience-preview`, body);
};

// List campaigns with their denormalized stats funnel.
// → { campaigns:[{ _id, name, status, templateName, language, stats, createdAt }] }
//   stats = { queued, sent, delivered, read, failed, skipped }
export const apiListCampaigns = (vendorId) =>
  API.get(`/api/vendor/${vendorId}/campaigns`);

// Create a draft campaign (Phase 2).
// payload: { name, audience:{ source, tags?, orderWindowDays?, minOrders?,
//   notOrderedSinceDays?, chattedWithinDays? }, templateName, language,
//   variables:string[], schedule?:{ mode:'now'|'scheduled', sendAt } } → { campaign }
// Backward compatible: a legacy { tags } payload (no audience) is mapped to
// audience:{ source:'tag', tags }.
export const apiCreateCampaign = (vendorId, payload = {}) => {
  const body = { ...payload };
  if (!body.audience && Array.isArray(body.tags)) {
    body.audience = { source: "contacts_tag", tags: body.tags };
  }
  return API.post(`/api/vendor/${vendorId}/campaigns`, body);
};

// Campaign detail + recipient breakdown.
// → { campaign, recipientCounts:{ pending, sent, delivered, read, failed, skipped },
//     sample:[{ phone, name, status, skipReason }] }
export const apiGetCampaign = (vendorId, campaignId) =>
  API.get(`/api/vendor/${vendorId}/campaigns/${campaignId}`);

// Send a single test message to a phone (the vendor's own number).
// payload: { phone } → { ok, reason? }
export const apiTestCampaign = (vendorId, campaignId, phone) =>
  API.post(`/api/vendor/${vendorId}/campaigns/${campaignId}/test`, { phone });

// Launch a draft campaign (Phase 2). With no schedule (or mode 'now') the
// campaign starts sending immediately; with a future schedule:{ mode:'scheduled',
// sendAt } it transitions to status 'scheduled' and starts at sendAt. → { campaign }
export const apiLaunchCampaign = (vendorId, campaignId, schedule) =>
  API.post(
    `/api/vendor/${vendorId}/campaigns/${campaignId}/launch`,
    schedule ? { schedule } : {}
  );

// Patch a campaign (rename / change status). payload: { name?, status? } → { campaign }
export const apiUpdateCampaign = (vendorId, campaignId, payload) =>
  API.patch(`/api/vendor/${vendorId}/campaigns/${campaignId}`, payload);

export const INBOX_PAGE_SIZE = 30;

export const apiFetchBrands = (params = {}) =>
  API.get(`/api/brands`, { params });

export const apiFetchBrandDirectory = (params = {}) =>
  API.get(`/api/brands/admin-directory`, { params });

export const apiFetchBrandById = (brandId) =>
  API.get(`/api/brands/${brandId}`);

export const apiFetchBrandFeaturedImageHistory = (brandId) =>
  API.get(`/api/brands/${brandId}/featured-image/history`);

// Resolve brands owned by a user (returns list so caller can pick first)
export const apiFetchBrandsByOwner = (vendorId) =>
  API.get(`/api/brands`, { params: { vendorId } });

export const apiCreateBrand = (brandData) =>
  API.post(`/api/brands`, brandData);

export const apiUpdateBrand = (brandId, brandData) =>
  API.put(`/api/brands/${brandId}`, brandData);

export const apiDeleteBrand = (brandId) =>
  API.delete(`/api/brands/${brandId}`);

export const apiToggleBrandStatus = (brandId) =>
  API.patch(`/api/brands/${brandId}/toggle-status`);

// Best-effort reclaim of a brand image from Cloudinary (pre-save orphan cleanup)
export const apiCleanupBrandMedia = (url) =>
  API.post(`/api/brands/media/cleanup`, { url });

export const apiRemoveBrandMedia = (brandId, type) =>
  API.delete(`/api/brands/${brandId}/media/${type}`);

export const apiCreateBrandMediaUploadSignature = (payload) =>
  API.post(`/api/brands/media/upload-signature`, payload);

export const apiUpdateFeaturedBrandsSetting = (settings) =>
  API.patch(`/api/brands/settings/featured-brands`, settings);

// ── Brand outlets (branches) ─────────────────────────────────────────────────
// Contract: /api/brands/:id/outlets. Lusuku staff create and delete outlets
// and edit location fields; the brand owner and their My Team helpers edit
// contact fields (phone, email, managerName), activation and hours. The body
// is built per role in components/BrandOutletFormModal (buildBrandOutletBody),
// so these helpers stay thin and every call carries the session token.
export const apiCreateBrandOutlet = (brandId, outletData) =>
  API.post(`/api/brands/${brandId}/outlets`, outletData);

export const apiGetBrandOutlets = (brandId) =>
  API.get(`/api/brands/${brandId}/outlets`);

export const apiUpdateBrandOutlet = (brandId, outletId, body) =>
  API.put(`/api/brands/${brandId}/outlets/${outletId}`, body);

export const apiDeleteBrandOutlet = (brandId, outletId) =>
  API.delete(`/api/brands/${brandId}/outlets/${outletId}`);

export const apiToggleBrandOutlet = (brandId, outletId) =>
  API.patch(`/api/brands/${brandId}/outlets/${outletId}/toggle-status`);

// Outlet hours live on the OUTLET, never on the parent brand. GET returns
// { openHours, specialHours, timezone, inherited, effective }; PUT takes
// { openHours, specialHours?, timezone? }; DELETE reverts to the brand's hours.
export const apiGetBrandOutletHours = (brandId, outletId) =>
  API.get(`/api/brands/${brandId}/outlets/${outletId}/hours`);

export const apiUpdateBrandOutletHours = (brandId, outletId, body) =>
  API.put(`/api/brands/${brandId}/outlets/${outletId}/hours`, body);

export const apiDeleteBrandOutletHours = (brandId, outletId) =>
  API.delete(`/api/brands/${brandId}/outlets/${outletId}/hours`);

export const apiResendBrandOwnerSetupLink = (brandId) =>
  API.post(`/api/brands/${brandId}/resend-owner-setup`);

export const apiCheckBrandOwnerEmail = ({ email, brandId } = {}) =>
  API.post(`/api/brands/check-owner-email`, { email, brandId });

// Get Restaurant Vendor Orders
export const fetchRestaurantVendorOrdersApi = (
  vendorId,
  page = 1,
  limit = 10,
  { outletId, ...listFilters } = {}
) =>
  API.get(`/weborders/restaurant-vendor-orders/${vendorId}/orders`, {
    params: withListFilters(withOutlet({ page, limit }, outletId), listFilters),
  });

// Get RetailerWholesaler Vendor Orders
export const fetchRetailerWholesalerVendorOrdersApi = (
  vendorId,
  page = 1,
  limit = 10,
  listFilters = {}
) =>
  API.get(`/weborders/retailer-wholesaler-vendor-orders/${vendorId}/orders`, {
    params: withListFilters({ page, limit }, listFilters),
  });

// Get Brand Vendor Orders. `outletId` narrows the list to orders fulfilled
// from one brand outlet (server: ?outletId=); omitted, every order is listed.
export const fetchBrandVendorOrdersApi = (
  vendorId,
  page = 1,
  limit = 10,
  { outletId, ...listFilters } = {}
) =>
  API.get(`/weborders/brand-vendor-orders/${vendorId}/orders`, {
    params: withListFilters(withOutlet({ page, limit }, outletId), listFilters),
  });

// Get retailerWholesaler Order Details
export const fetchRetailerWholesalerOrderDetailsApi = (orderId, vendorId) =>
  API.get(
    `/weborders/retailer-wholesaler-order-details/${orderId}/${vendorId}`
  );

//Update Order Details Status
export const updateRetailerWholesalerOrderStatusApi = (orderId, status) =>
  API.put(`/weborders/update_retailer_wholesaler_order_status/${orderId}`, {
    status,
  });

// Healthcare Vendor API Functions
// Get Healthcare Vendor Orders
export const fetchHealthcareVendorOrdersApi = (
  vendorId,
  page = 1,
  limit = 10,
  { outletId, ...listFilters } = {}
) =>
  API.get(`/weborders/healthcare-vendor-orders/${vendorId}/orders`, {
    params: withListFilters(withOutlet({ page, limit }, outletId), listFilters),
  });

// Get Healthcare Vendor Stats
export const fetchHealthcareVendorStatsApi = (vendorId, { outletId } = {}) =>
  API.get(`/weborders/healthcare-vendor-stats/${vendorId}`, { params: withOutlet({}, outletId) });

//Restaurant Profile
export const apiFetchRestaurantProfile = (vendorId) =>
  API.get(`/restaurants/restaurant-profile/${vendorId}`);
export const apiUpdateRestaurantProfile = (vendorId, formData) =>
  API.put(`/restaurants/restaurant-profile/${vendorId}`, formData);
export const apiUpdateOpenHours = (vendorId, openHoursData) =>
  API.patch(`/restaurants/${vendorId}/open-hours`, openHoursData);
export const apiGetOpenStatus = (vendorId) =>
  API.get(`/restaurants/${vendorId}/open-status`);

// ── Business hours, any vendor type ─────────────────────────────────────────
// One helper instead of a per-vertical copy. The backend mounts the SAME
// contract under each vendor's base path, so only the prefix differs.
//
// This also replaces the broken calls in BrandManagement/OutletManagementBrand,
// which passed a single object into the two-arg restaurant helper and produced
// `PATCH /restaurants/[object Object]/open-hours` with an undefined body — brand
// hours have never actually saved.
const HOURS_BASE_BY_TYPE = {
  restaurant: "/restaurants",
  healthcare: "/healthcare",
  retailer: "/rw",
  brand: "/api/brands",
};

const hoursBase = (vendorType) => {
  const base = HOURS_BASE_BY_TYPE[String(vendorType || "").toLowerCase()];
  if (!base) throw new Error(`Unknown vendor type for business hours: ${vendorType}`);
  return base;
};

export const apiUpdateVendorOpenHours = (vendorType, vendorId, payload) =>
  API.patch(`${hoursBase(vendorType)}/${vendorId}/open-hours`, payload);

export const apiGetVendorOpenStatus = (vendorType, vendorId) =>
  API.get(`${hoursBase(vendorType)}/${vendorId}/open-status`);

// Retailer/Wholesaler Profile
export const apiFetchRetailerWholesalerProfile = (vendorId) =>
  API.get(`/rw/retailer-wholesaler-profile/${vendorId}`);

//Get retialers and wholesalers
export const apiFetchRetailersWholesalers = (vendorId, canViewDirectory) =>
  canViewDirectory
    ? API.get("/rw/admin-directory")
    : API.get("/rw/retailers-wholesalers", { params: { vendorId } });

export const apiUpdateRetailerWholesalerProfile = (vendorId, formData) =>
  API.put(`/rw/update-retailer-wholesaler-profile/${vendorId}`, formData);

// POS Terminal — set a cashier PIN (stored as SHA-256 hash on the vendor profile)
export const apiSetCashierPin = (data) =>
  API.put(`/api/pos/cashier-pin`, data);

// Get sections or conditionally get sections by vendorId if vendorId is provided
// export const apiFetchSections = () => API.get('/restaurants/menu-sections');
// `outletId` reads the menu as it stands at that branch (4 Oct 2026): its
// sold out, its price, and only what a curated branch menu sells.
export const apiFetchSections = (vendorId, { outletId } = {}) =>
  API.get(
    `/restaurants/menu-sections${vendorId ? `?vendorId=${vendorId}` : ""}`,
    ...(outletId ? [{ params: { outletId } }] : [])
  );

//Update Section
export const apiUpdateSection = (sectionId, updatedSectionData) =>
  API.patch(`/restaurants/menu-sections/${sectionId}`, updatedSectionData);

export const apiFetchAmazonReadyMedia = (vendorId, refreshKey, shootSession) =>
  API.get(`/api/media/shop/${vendorId}/amazon-ready`, {
    params: {
      ...(refreshKey ? { t: refreshKey } : {}),
      ...(shootSession ? { shootSession } : {}),
    },
  });

export const apiFetchShopMedia = (vendorId, refreshKey, shootSession) =>
  API.get(`/api/media/shop/${vendorId}`, {
    params: {
      ...(refreshKey ? { t: refreshKey } : {}),
      ...(shootSession ? { shootSession } : {}),
    },
  });

/**
 * Create a short-lived shoot session token for the current vendor.
 * Used to authorise a mobile device (possibly logged in as a different user)
 * to shoot product images for this dashboard vendor.
 */
export const apiCreateShootSession = (body = {}) =>
  API.post(`/api/media/shoot-session`, body);

/**
 * Server-side background re-enhance of an existing ShopMedia asset.
 * Used by the dashboard picker "Needs review → Clean background" flow.
 */
export const apiEnhanceMedia = (mediaId, body = {}) =>
  API.post(`/api/media/${mediaId}/enhance`, body);

/**
 * Soft-delete (archive) a ShopMedia asset.
 */
export const apiArchiveMedia = (mediaId) =>
  API.delete(`/api/media/${mediaId}`);
// // Create Product
// export const apiAddProduct = (productData, userId) => API.post(`/restaurants/menu-products/${userId}`, productData);

// Create Collection
// export const apiAddCollection = (collectionData) => API.post('/api/menu-collections', collectionData); //Not in use

//Get Listing
export const apifetchListing = (listing_id) =>
  API.get(`/posts/listing/${listing_id}`);

export const apifetchListings = (userId, page) =>
  API.get(`/posts/dashboard?userId=${userId}&page=${page}`);

// Get Deactivated Listings
export const apiGetDeactivatedListings = (user_id) =>
  API.get(`/posts/deactivated-listings/${user_id}`);

// Get Out of stock Listings
export const apiGetOuttaStockListings = (user_id) =>
  API.get(`/posts/out-of-stock-listings/${user_id}`);

//Get All Orders ~ With Pagination (optionally filtered by vendorId)
// `filters` is { delivery, payment, source, vendorType, from, to }, applied
// on the server (utils/orderListFilters.js) so they cut across pages. Empty
// values are left out of the query string.
export const fetchPaginatedOrders = (page, vendorId, filters = {}) => {
  const params = new URLSearchParams({ page: String(page) });
  if (vendorId) params.set("vendorId", vendorId);
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });
  return API.get(`/weborders/all_paginated?${params.toString()}`);
};

// Get Vendor Order Details to process
export const getVendorOrderDetailsToProcessApi = (orderId, vendorId) =>
  API.get(`/weborders/vendor/${orderId}/${vendorId}`);

// GET VENDOR'S ORDERS
export const fetchVendorOrders = (vendorId, page) =>
  API.get(`/weborders/vendor/${vendorId}?page=${page}`);

// Search Paginated Orders (optionally filtered by vendorId)
export const searchOrders = (searchQuery, vendorId) =>
  API.get(
    `/weborders/search_orders?searchQuery=${searchQuery.search}${vendorId ? `&vendorId=${vendorId}` : ""
    }`
  );
// Search Vendor Paginated Orders
export const searchVendorOrders = (vendorId, searchQuery) =>
  API.get(
    `/weborders/search_vendor_orders/${vendorId}?searchQuery=${searchQuery}`
  );
// Get All Orders ~ Without Paginations
export const fetchListingsNoLimit = (userId, role) =>
  API.get(`/posts/all/${userId}/${role}`);

// Search Listings
export const fetchListingsBySearch = (searchQuery) =>
  API.get(
    `/posts/search/${searchQuery.user_id}?searchQuery=${searchQuery.search || "none"
    }`
  );

// Search Clients
export const fetchClientsBySearch = (searchQuery) =>
  API.get(
    `/user/search/${searchQuery.user_id}?searchQuery=${searchQuery.searchClient || "none"
    }`
  );

// Create Rider
export const createRiderApi = (riderData, creatorId) =>
  API.post(`/riders/${creatorId}`, riderData);

// Get all riders by Super Admin
export const getAllRidersApi = () => API.get("/riders/all-riders");

// Update Rider
export const updateRiderApi = (riderId, riderData) =>
  API.put(`/riders/${riderId}/update-by-admin`, riderData);

// Create Listing
export const apiCreateListing = (newListing, userId) =>
  API.post(`/posts/${userId}`, newListing);

export const apiCreatePromoListing = (newPromoListing, userId) =>
  API.post(`/posts/promo/${userId}`, newPromoListing);

export const apiUpdateListing = (listingId, updatedListing, userId) =>
  API.patch(`/posts/update/${listingId}/${userId}`, updatedListing);

// DEACTIVATE LISTING
export const apiDeactivateListing = (listingId, user_id) =>
  API.patch(`/posts/deactivate/${listingId}/${user_id}`);

// REACTIVATE LISTING
export const apiReactivateListing = (listingId, user_id) =>
  API.patch(`/posts/reactivate/${listingId}/${user_id}`);

// Delete Listing
export const deleteListing = (listingId, userId) =>
  API.delete(`/posts/${listingId}/${userId}`);

// GET ALL ORDERS NO LOADER
export const fetchOrders = () => API.get("/weborders/all");

// MOBILE END_POINTS
// export const fetchMobileOrder = (id) => API.get(`/mobile/${id}`);
export const fetchMobileUsers = (userId, role) =>
  API.get(`/mobileUser/all/${userId}/${role}`);

// ── Staff lifecycle: suspend / reinstate / offboard / restore ───────────────
// Replaces the old `deleteStaffUser` helper. That one hit /user/deleteStaff,
// which soft-deleted the account with no reason, left the person's sessions
// open and told the operator the action was irreversible. One endpoint now
// carries all four transitions; the reason is mandatory and is recorded
// against the acting super user.
//   action: "SUSPEND" | "REINSTATE" | "OFFBOARD" | "RESTORE"
export const apiStaffLifecycle = (staff_id, userId, { action, reason }) =>
  API.post(`/user/staff/lifecycle/${userId}/${staff_id}`, { action, reason });

// That staff member's access history: every suspend, reinstate, offboard and
// restore, with the actor and the reason they gave.
export const apiStaffLifecycleHistory = (staff_id, userId, limit = 50) =>
  API.get(`/user/staff/lifecycle/${userId}/${staff_id}`, { params: { limit } });

// Delete mobile user
export const apiDeleteMobileUser = (client_id, userId) =>
  API.delete(`/user/deleteMobileUser/${client_id}/${userId}`);

export const fetchMobileUser = (id, userId, role) =>
  API.get(`/mobileUser/${id}/${userId}/${role}`);

export const signIn = (formData) => API.post("/user/signin", formData);
export const signUp = (formData) => API.post("/user/signup", formData);
export const partnerSignIn = (formData) => API.post("/api/3pl-partners/login", formData);

// ── 3PL partner console ───────────────────────────────────────────────────
// Every one of these is partner-scoped on the server (auth +
// assertPartnerOwnership + a staff-permission check), so the partnerId in the
// path must be the caller's own. Passing someone else's is a 403, not a leak.
const threePL = (partnerId) => `/api/3pl-partners/${partnerId}`;

// Cash control. The overview is the partner's cash-at-risk board.
export const apiFetchPartnerCod = (partnerId) => API.get(`${threePL(partnerId)}/cod`);
export const apiFetchPartnerDashboard = (partnerId) =>
  API.get(`${threePL(partnerId)}/dashboard`);
export const apiUpdatePartnerCodPolicy = (partnerId, defaultRiderThreshold) =>
  API.put(`${threePL(partnerId)}/cod/policy`, { defaultRiderThreshold });
export const apiUpdateRiderCodThreshold = (partnerId, riderId, threshold) =>
  API.patch(`${threePL(partnerId)}/cod/riders/${riderId}`, { threshold });
export const apiUpdateRiderVehicle = (partnerId, riderId, body) =>
  API.patch(`${threePL(partnerId)}/riders/${riderId}/vehicle`, body);

// Money
export const apiFetchPartnerPayouts = (partnerId, params = {}) =>
  API.get(`${threePL(partnerId)}/payouts`, { params });
export const apiFetchPartnerIncentives = (partnerId) =>
  API.get(`${threePL(partnerId)}/incentives`);

// Network
export const apiFetchPartnerHubs = (partnerId) => API.get(`${threePL(partnerId)}/hubs`);
export const apiAddPartnerHub = (partnerId, hub) => API.post(`${threePL(partnerId)}/hubs`, hub);
export const apiAddPartnerServiceArea = (partnerId, area) =>
  API.post(`${threePL(partnerId)}/service-areas`, area);
export const apiFetchPartner = (partnerId) => API.get(threePL(partnerId));

// Offer response. A vendor handing an order to a partner is an OFFER now, not a
// done deal, so the partner answers it. Accept freezes the delivery promise and
// the message that comes back already states it; decline returns the order to
// the vendor, and the reason is optional context for them, not a condition.
export const apiAcceptPartnerOffer = (partnerId, orderId) =>
  API.post(`${threePL(partnerId)}/orders/${orderId}/accept`);
export const apiDeclinePartnerOffer = (partnerId, orderId, reason) =>
  API.post(`${threePL(partnerId)}/orders/${orderId}/decline`, { reason });

// Dispatch automation
export const apiFetchPartnerAutoAssign = (partnerId) =>
  API.get(`${threePL(partnerId)}/auto-assign`);
export const apiTogglePartnerAutoAssign = (partnerId, enabled) =>
  API.post(`${threePL(partnerId)}/auto-assign/toggle`, { enabled });
export const apiUpdatePartnerAutoAssignSettings = (partnerId, settings) =>
  API.put(`${threePL(partnerId)}/auto-assign/settings`, settings);

// Account security
export const apiPartner2FASetup = () => API.post("/api/3pl-partners/2fa/setup");
// `totpCode`, not `token`: both controllers destructure req.body.totpCode, so a
// body keyed `token` reads as an empty code and always answers "Invalid code".
export const apiPartner2FAConfirm = (totpCode) =>
  API.post("/api/3pl-partners/2fa/confirm", { totpCode });
export const apiPartner2FADisable = (totpCode) =>
  API.post("/api/3pl-partners/2fa/disable", { totpCode });
export const staffSignIn = (formData) => API.post("/api/3pl-staff/login", formData);

// Staff / Admin 2FA
export const apiStaff2FAVerifyLogin = (data) => API.post("/user/2fa/verify-login", data);
export const apiClearStaff2FATrustedDevice = () =>
  API.post("/user/2fa/clear-trusted-device");
export const apiStaff2FATrustedDeviceStatus = () =>
  API.get("/user/2fa/trusted-device-status");
export const apiStaff2FASetup = () => API.post("/user/2fa/setup");
export const apiStaff2FAConfirm = (totpCode) => API.post("/user/2fa/confirm", { totpCode });
export const apiStaff2FADisable = (totpCode) => API.post("/user/2fa/disable", { totpCode });

// 3PL Staff Management API Functions
export const getPartnerStaff = () => API.get("/api/3pl-staff");
export const getStaffById = (staffId) => API.get(`/api/3pl-staff/${staffId}`);
export const createPartnerStaff = (staffData) => API.post("/api/3pl-staff", staffData);
export const updatePartnerStaff = (staffId, staffData) => API.put(`/api/3pl-staff/${staffId}`, staffData);
export const deletePartnerStaff = (staffId) => API.delete(`/api/3pl-staff/${staffId}`);
export const updateStaffPassword = (staffId, passwordData) => API.put(`/api/3pl-staff/${staffId}/password`, passwordData);
export const getMyStaffProfile = () => API.get("/api/3pl-staff/me");
export const updateMyStaffProfile = (profileData) => API.put("/api/3pl-staff/me", profileData);

// Create Client profile
export const createClientProfileApi = (formData, userId, role) =>
  API.post(`/user/client/${userId}`, formData);

// Create Staff Profile
export const createStaffProfileApi = (formData, userId) =>
  API.post(`/user/staff/${userId}`, formData);

// ── Staff creation conflicts ────────────────────────────────────────────────
// Creating a staff account fails with 409 when the address already belongs to
// somebody. The response names WHO (`code`, `existing`) and WHAT can be done
// (`actions`), and these three back those actions.

// Pre-flight: is this address free, and if not, whose is it? Runs the same
// classification the create endpoint runs, so the form can never promise
// something Create would then refuse. POST keeps the address out of URL logs.
export const apiCheckStaffEmail = (userId, email) =>
  API.post(`/user/staff/email-check/${userId}`, { email });

// Re-send (and thereby revoke and replace) a staff member's set-password link.
export const apiResendStaffInvite = (userId, targetId) =>
  API.post(`/user/staff/invite/${userId}/${targetId}`);

// Give an account that already exists (typically a shopper who used their work
// address) staff access, instead of its email being unusable forever.
export const apiPromoteClientToStaff = (userId, targetId, user_role) =>
  API.post(`/user/staff/promote/${userId}/${targetId}`, { user_role });

// Remove a leftover platform role from a NON-staff account (a vendor carrying a
// stray ADMIN). Normalises it to CONSUMER and clears any overrides.
export const apiClearPlatformRole = (userId, targetId) =>
  API.post(`/user/staff/clear-role/${userId}/${targetId}`);

// Convert an account's TYPE. toType is "LUSUKU STAFF" or "CLIENT". confirmName
// must match the account's name or email. When converting to staff, user_role
// is the staff role to grant. The server refuses to strand a live storefront.
export const apiConvertAccountType = (userId, targetId, payload) =>
  API.post(`/user/staff/convert-type/${userId}/${targetId}`, payload);

export const fetchUsers = (userId, role, { includeOffboarded = false } = {}) =>
  API.get(`/user/staff/all/${userId}/${role}`, {
    params: includeOffboarded ? { includeOffboarded: true } : {},
  });

// Find user by id ~ findUser
export const fetchUser = (editedUserId, staff_id, role) =>
  API.get(`/user/${editedUserId}/${staff_id}/${role}`);

// Update Agent Role ~ updateUserRole
export const updateUserRole = (userId, role, agentId) =>
  API.put(`/user/${userId}/${agentId}`, role);

export const submitVendorRegistration = (vendorFormData) =>
  API.post("/seller/register-seller", vendorFormData);

// Get sellers
export const fetchSellers = (staff_id) =>
  API.get(`/seller/get-sellers/${staff_id}`);

// Pending-verification queue for the navbar bell (count + newest pending
// sellers in the active country). Staff reviewers only; 403 for everyone else.
export const apiGetSellerReviewQueue = () => API.get(`/seller/review-queue`);

// Get seller Details
export const fetchSellerDetail = (seller_id) =>
  API.get(`/seller/get-seller/${seller_id}`);

// Delete seller
export const softDeleteSellerApi = (seller_id, staff_id) =>
  API.delete(`/seller/soft-delete-seller/${seller_id}/${staff_id}`);

// Delete seller
export const deleteSellerApi = (seller_id, staff_id) =>
  API.delete(`/seller/delete-seller/${seller_id}/${staff_id}`);

// Get all active categories
// view=admin: every active category, empty ones included, pictures untouched.
// Without it the server answers as the storefront rail (empty ones hidden,
// default pictures filled in), which is what the shop apps want.
export const fetchActiveCategories = () =>
  API.get("/category/categories", { params: { view: "admin" } });

// Get deactivated categories
export const fetchDeactivatedCategories = () =>
  API.get("/category/deactivated-categories");

// Get delivery zones
export const fetchDeliveryZones = () => API.get("/deliveryzone");

// Delete delivery zone
export const deleteZone = (zone_id) =>
  API.delete(`/deliveryzone/delete/${zone_id}`);

// Create delivery zone
export const createDeliveryZone = (zoneData) =>
  API.post("/deliveryzone/create", zoneData);

// Fetch delivery zone
export const fetchDeliveryZone = (zone_id) =>
  API.get(`/deliveryzone/${zone_id}`);

// Update delivery zone
export const patchDeliveryZone = (zone_id, updatedZoneData) =>
  API.patch(`/deliveryzone/update/${zone_id}`, updatedZoneData);

// Verify seller
export const verifySellerApi = (seller_id, staff_id, data) =>
  API.put(`/seller/verify-seller/${seller_id}/${staff_id}`, data);

// Reactivate seller
export const reactivateSellerApi = (seller_id, staff_id, data) =>
  API.put(`/seller/reactivate-seller/${seller_id}/${staff_id}`, data);

// Resend verification email
export const resendVerificationEmailApi = (seller_id) =>
  API.post(`/seller/resend-verification-email/${seller_id}`);

// Update seller profile (Super Admin only)
export const updateSellerProfileApi = (seller_id, data) =>
  API.put(`/seller/update-profile/${seller_id}`, data);

// Update Delivery Status (Including individual order items)
export const updateDeliveryStatusApi = (orderId, itemId, delivery_status) =>
  API.put(`/weborders/update_delivery_status/${orderId}/${itemId}`, {
    delivery_status,
  });

// Update Restaurant Delivery Status
export const updateRestaurantOrderDeliveryStatusApi = (
  restaurantOrderId,
  delivery_status,
  // { reason, note } when this is a cancellation. The server refuses a
  // cancelling status without a reason and records who sent it.
  cancellation
) =>
  API.put(
    `/weborders/update_restaurant_order_delivery_status/${restaurantOrderId}`,
    {
      delivery_status,
      ...(cancellation ? { cancellation } : {}),
    }
  );

// Update Package Delivery Status
export const updatePackageOrderDeliveryStatusApi = (
  packageOrderId,
  delivery_status
) =>
  API.put(`/weborders/update_package_order_delivery_status/${packageOrderId}`, {
    delivery_status,
  });

// Update Overall Delivery Status. `body` is either { itemUpdate: [{ itemId,
// delivery_status }] } for named lines, or { applyToAll: { delivery_status } }
// to move every line on the order, resolved server-side: the way an order is
// cancelled, so a line the client cannot name (its listing was deleted) can
// no longer make the whole order uncancellable.
export const updateOverallOrderDeliveryStatusApi = (orderId, body) =>
  API.put(`/weborders/update_overallOrder_delivery_status/${orderId}`, body);

// Assign Individual Order Items to Delivery Agent
export const assignOrderToDeliveryAgentApi = (
  orderId,
  riderId,
  itemId,
  delivery_status
) =>
  API.put(`/weborders/assign_order_to_delivery_agent/${orderId}/${itemId}`, {
    delivery_status,
    riderId,
  });

// Assign Restaurant Order to Delivery Agent
export const assignRestaurantOrderToDeliveryAgentApi = (
  restaurantOrderId,
  riderId,
  delivery_status,
  forceAssign = false
) =>
  API.put(
    `/weborders/assign_restaurant_order_to_delivery_agent/${restaurantOrderId}`,
    {
      delivery_status,
      riderId,
      ...(forceAssign && { forceAssign: true }),
    }
  );

// Re-assign Restaurant Order to Delivery Agent
export const reassignRestaurantOrderToDeliveryAgentApi = (
  restaurantOrderId,
  riderId,
  delivery_status,
  reassign
) =>
  API.put(
    `/weborders/re_assign_restaurant_order_to_delivery_agent/${restaurantOrderId}`,
    {
      delivery_status,
      riderId,
      reassign,
    }
  );

// Assign Package Order to Delivery Agent
export const assignPackageOrderToDeliveryAgentApi = (
  packageOrderId,
  riderId,
  delivery_status
) =>
  API.put(
    `/weborders/assign_package_order_to_delivery_agent/${packageOrderId}`,
    {
      delivery_status,
      riderId,
    }
  );

// Mark ordered Items/cart as paid
export const markOrderAsPaidApi = (orderId) =>
  API.put(`/weborders/${orderId}/paid`);

export const apiGetOrderPaymentRecovery = (orderId) => API.get(`/weborders/${orderId}/payment/recovery`);
// The order as a message for the vendor, composed server-side (copy or WhatsApp).
export const apiGetOrderVendorHandoff = (orderId) => API.get(`/weborders/${orderId}/vendor-handoff`);
export const apiRecoverOrderPayment = (orderId, body) => API.post(`/weborders/${orderId}/payment/recovery`, body);
export const apiReconcileOrderPayment = (orderId) => API.post(`/weborders/${orderId}/payment/recovery/reconcile`);

// What MTN and Flutterwave said about each charge (views/PaymentProviderLogView.js).
// params: { orderId?, reference?, provider?, outcome?, operation?, hours?, limit?, before? }
export const apiListPaymentProviderLog = (params) => API.get(`/api/payment-provider-log`, { params });
// One ticket order from reservation to the gate (views/Ticketing/TicketOrderDetails.js).
export const apiGetTicketOrderLifecycle = (orderId) => API.get(`/api/ticketing/orders/${orderId}/lifecycle`);
export const apiPaymentProviderLogSummary = (params) => API.get(`/api/payment-provider-log/summary`, { params });

// A Lusuku agent received cash (components/CodCashReceiptDialog.js).
// body: { channel: "AGENT_MTN_DEPOSIT"|"AGENT_CASH_AT_HAND", mtnTransactionId?, note? }
export const apiRecordOrderCashReceipt = (orderId, body) =>
  API.post(`/weborders/${orderId}/cod/agent-receipt`, body);
// body adds { amount, receiptKey }
export const apiReceiveRiderCash = (riderId, body) =>
  API.post(`/api/riders/cod/${riderId}/agent-receipt`, body);

// Verify seller listing
export const apiVerifySellerListing = (listingId, user_id, verifyListing) =>
  API.patch(`/posts/verify/${listingId}/${user_id}`, verifyListing);

// UnVerify seller listing
export const apiUnverifySellerListing = (listingId, user_id, verifyListing) =>
  API.patch(`/posts/unverify/${listingId}/${user_id}`, verifyListing);

// Delete a rider
export const deleteRiderApi = (rider_id, staff_id) =>
  API.delete(`/riders/delete-rider/${rider_id}/${staff_id}`);

// CRUD FOR RESTAURANT VENDOR RIDERS
// Vendor-agnostic, reachability-enriched list used by order assignment. Unlike
// the type-specific CRUD endpoints, this accepts a platform operator viewing
// an order on behalf of its actual vendor.
export const getVendorRidersApi = (vendorId) =>
  API.get(`/riders/vendor/${encodeURIComponent(vendorId)}/riders`);
export const getRestaurantRidersApi = (vendorId) =>
  API.get(`/riders/restaurant-vendor/${vendorId}/riders`);
export const createRestaurantRiderApi = (riderData, vendorId) =>
  API.post(`/riders/restaurant-vendor/${vendorId}/riders`, riderData);
export const updateRestaurantRiderApi = (riderData, vendorId, riderId) =>
  API.put(`/riders/restaurant-vendor/${vendorId}/riders/${riderId}`, riderData);
export const deleteRestaurantRiderApi = (vendorId, riderId) =>
  API.delete(`/riders/restaurant-vendor/${vendorId}/riders/${riderId}`);

// CRUD FOR BRAND OWNER VENDOR RIDERS
export const getBrandOwnerRidersApi = (vendorId) =>
  API.get(`/riders/brand-owner/${vendorId}/riders`);
export const createBrandOwnerRiderApi = (riderData, vendorId) =>
  API.post(`/riders/brand-owner/${vendorId}/riders`, riderData);
export const updateBrandOwnerRiderApi = (riderData, vendorId, riderId) =>
  API.put(`/riders/brand-owner/${vendorId}/riders/${riderId}`, riderData);
export const deleteBrandOwnerRiderApi = (vendorId, riderId) =>
  API.delete(`/riders/brand-owner/${vendorId}/riders/${riderId}`);

// CRUD FOR RETAILER WHOLESALER RIDERS
export const getRetailerRidersApi = (vendorId) =>
  API.get(`/riders/retailer-wholesaler-vendor/${vendorId}/riders`);
export const createRetailerRiderApi = (riderData, vendorId) =>
  API.post(`/riders/retailer-wholesaler-vendor/${vendorId}/riders`, riderData);
export const updateRetailerRiderApi = (riderData, vendorId, riderId) =>
  API.put(
    `/riders/retailer-wholesaler-vendor/${vendorId}/riders/${riderId}`,
    riderData
  );
export const deleteRetailerRiderApi = (vendorId, riderId) =>
  API.delete(
    `/riders/retailer-wholesaler-vendor/${vendorId}/riders/${riderId}`
  );

// CRUD FOR HEALTHCARE VENDOR RIDERS
export const getHealthcareRidersApi = (vendorId) =>
  API.get(`/riders/healthcare-vendor/${vendorId}/riders`);
export const createHealthcareRiderApi = (riderData, vendorId) =>
  API.post(`/riders/healthcare-vendor/${vendorId}/riders`, riderData);
export const updateHealthcareRiderApi = (riderData, vendorId, riderId) =>
  API.put(`/riders/healthcare-vendor/${vendorId}/riders/${riderId}`, riderData);
export const deleteHealthcareRiderApi = (vendorId, riderId) =>
  API.delete(`/riders/healthcare-vendor/${vendorId}/riders/${riderId}`);
// API calls for managing retailer and wholesaler sections
// Create a section
export const apiCreateRetailerWholesalerSection = (sectionData, userId) =>
  API.post(`/rw/rw-create-sections`, { ...sectionData, userId });

// Fetch sections
export const apiFetchRetailerWholesalerSections = (vendorId) =>
  API.get(`/rw/rw-get-sections?vendorId=${vendorId}`);

// Fetch Brand sections with products (for in-house order menu)
export const apiFetchBrandSections = (brandId, { outletId } = {}) =>
  API.get(`/api/brands/${brandId}/sections`, ...(outletId ? [{ params: { outletId } }] : []));

// Update a section
export const apiUpdateRetailerWholesalerSection = (
  sectionId,
  updatedSectionData
) => API.put(`/rw/rw-update-section/${sectionId}`, updatedSectionData);

// Delete a rw section
export const apiDeleteRetailerWholesalerSection = (sectionId) =>
  API.delete(`/rw/del-rw-section/${sectionId}`);

// Fetch retailers and wholesalers.
// Admins/platform staff must use /rw/admin-directory — the permissioned route
// that grants platform-directory scope and returns ALL vendors. The plain
// /rw/retailers-wholesalers route only returns a vendor the caller acts for,
// so passing another vendor's id there 403s ("vendor list scope").
export const apiFetchRetailerWholesalers = (vendorId, canViewDirectory) =>
  canViewDirectory
    ? API.get("/rw/admin-directory")
    : API.get("/rw/retailers-wholesalers", { params: { vendorId } });

// COD product-price policy (max unit price eligible for Cash on Delivery).
// GET is available to any authenticated dashboard user (product forms read it);
// PUT is admin-gated server-side.
// ── Settings menu (31 Oct 2026): what the caller may do, who changed what, and
// the settings that had no screen before. Guards: middleware/settingsAccess.js.
export const apiPlatformSettingsAccess = () => API.get("/api/platform-settings/access");
export const apiPlatformSettingsHistory = (keys = []) =>
  API.get("/api/platform-settings/history", { params: { keys: keys.join(",") } });
export const apiGetSupportSla = () => API.get("/api/platform-settings/support-sla");
export const apiUpdateSupportSla = (sla) => API.put("/api/platform-settings/support-sla", { sla });
export const apiGetPlatformFiscalIdentity = (country) =>
  API.get("/api/platform-settings/fiscal-identity", { params: country ? { country } : {} });
export const apiUpdatePlatformFiscalIdentity = (identity, country) =>
  API.put("/api/platform-settings/fiscal-identity", { identity, ...(country ? { country } : {}) });
export const apiGetMinimumOrderValue = (country) =>
  API.get("/api/minimum-order-value", { params: country ? { country } : {} });
export const apiUpdateMinimumOrderValue = (minimumOrderValue, country) =>
  API.put("/api/minimum-order-value", { minimumOrderValue, ...(country ? { country } : {}) });
// Growth offers: first-order free delivery, its cap and referrals, per market.
// A field sent as null goes back to the server default.
export const apiGetGrowthOfferSettings = (country) =>
  API.get("/api/growth/settings", { params: country ? { country } : {} });
export const apiUpdateGrowthOfferSettings = (patch, country) =>
  API.put("/api/growth/settings", { ...patch, ...(country ? { country } : {}) });

// Order together (group baskets): per-market switch, members and windows.
export const apiGetGroupOrderingSettings = (country) =>
  API.get("/api/group-baskets/settings", { params: country ? { country } : {} });
export const apiUpdateGroupOrderingSettings = (patch, country) =>
  API.put("/api/group-baskets/settings", { ...patch, ...(country ? { country } : {}) });

export const apiGetCodPolicy = () => API.get("/api/cod-policy");
export const apiUpdateCodPolicy = (maxProductAmount) =>
  API.put("/api/cod-policy", { maxProductAmount });

// Artwork templates — one downloadable PDF per image upload slot, so a logo,
// a 1440x175 banner and a 1000x1000 product shot each get the canvas that fits.
// Stored on Cloudinary as raw assets, so replacing one needs no deploy.
// Reads are open to any authenticated dashboard user (every vendor upload
// screen offers its slot's template); writes are admin-gated server-side.
export const apiGetArtworkTemplateSlots = () =>
  API.get("/api/artwork-templates/slots");
export const apiGetArtworkTemplates = () => API.get("/api/artwork-templates");
export const apiUploadArtworkTemplate = (slotKey, dataUrl, fileName, notes) =>
  API.put(`/api/artwork-templates/${slotKey}`, { dataUrl, fileName, notes });
export const apiRemoveArtworkTemplate = (slotKey) =>
  API.delete(`/api/artwork-templates/${slotKey}`);

// Create a product
export const apiCreateRetailerWholesalerProduct = (productData) =>
  API.post(`/rw/create-vendor-product`, productData);

// Fetch product details
export const apiFetchRetailerWholesalerProductDetails = (productId) =>
  API.get(`/rw/rw-product/${productId}`);

// Update a product
export const apiUpdateRetailerWholesalerProduct = (
  productId,
  updatedProductData
) => API.put(`/rw/product/${productId}`, updatedProductData);

// Update a product as staff (catalog quality control) — admin-authorized
// route; lets staff polish any vendor's listing. Audit-trailed server-side.
export const apiAdminUpdateRetailerWholesalerProduct = (
  productId,
  updatedProductData
) => API.put(`/rw/admin/product/${productId}`, updatedProductData);

// Delete a product
export const apiDeleteRetailerWholesalerProduct = (productId) =>
  API.delete(`/rw/del-rw-product/${productId}`);

// Option Group API functions
export const apiRestaurantCreateOptionGroup = (optionGroupData) =>
  API.post(`/menu/create-restaurant-option-group`, optionGroupData);
export const apiFetchOptionGroupsRestaurant = (restaurantId) =>
  API.get(`/menu/get-restaurant-option-groups?restaurantId=${restaurantId}`); //Reused endpoint
export const apiUpdateOptionGroup = (optionGroupId, updatedOptionGroupData) =>
  API.patch(`/menu/option-group/${optionGroupId}`, updatedOptionGroupData);
export const apiDeleteOptionGroup = (optionGroupId) =>
  API.delete(`/menu/option/${optionGroupId}`);

// Option API functions
export const apiCreateRestaurantOption = (optionData) =>
  API.post(`/menu/create-restaurant-option`, optionData);
export const apiFetchOptionsRestaurants = (restaurantId) =>
  API.get(`/menu/restaurant-options/${restaurantId}`);
export const apiUpdateOption = (optionId, updatedOptionData) =>
  API.put(`/menu/option/${optionId}`, updatedOptionData);
export const apiDeleteOption = (optionId) =>
  API.delete(`/menu/del-option/${optionId}`);

// Retailer/Wholesaler Option Groups
// Retailer/Wholesaler Options
export const apiCreateRetailerWholesalerOption = (optionData) =>
  API.post("/menu/rwOptions", optionData);
export const apiFetchRetailerWholesalerOptions = (vendorId) =>
  API.get(`/menu/get-rwOptions/${vendorId}`);
export const apiUpdateRetailerWholesalerOption = (
  optionId,
  updatedOptionData
) => API.put(`/menu/rwOptions/${optionId}`, updatedOptionData);
export const apiDeleteRetailerWholesalerOption = (optionId) =>
  API.delete(`/menu/rwOptions/${optionId}`);

// Retailer/Wholesaler Option Groups
export const apiCreateRetailerWholesalerOptionGroup = (optionGroupData) =>
  API.post("/menu/rwOptionGroups", optionGroupData);
export const apiFetchRetailerWholesalerOptionGroups = (vendorId) =>
  API.get(`/menu/rwOptionGroups/${vendorId}`);
export const apiUpdateRetailerWholesalerOptionGroup = (
  optionGroupId,
  updatedOptionGroupData
) => API.put(`/menu/rwOptionGroups/${optionGroupId}`, updatedOptionGroupData);
export const apiDeleteRetailerWholesalerOptionGroup = (optionGroupId) =>
  API.delete(`/menu/rwOptionGroups/${optionGroupId}`);

export const apiDeleteRetailerWholesaler = (id) =>
  API.delete(`/retailer-wholesaler/${id}`);

// Healthcare API functions
export const apiCreateHealthcareSection = (sectionData, userId) =>
  API.post(`/healthcare/create-healthcare-section`, { ...sectionData, userId });

export const apiFetchHealthcareSections = (vendorId) =>
  API.get(`/healthcare/healthcare-sections?vendorId=${vendorId}`);

export const apiUpdateHealthcareSection = (sectionId, updatedSectionData) =>
  API.put(`/healthcare/healthcare-section/${sectionId}`, updatedSectionData);

export const apiDeleteHealthcareSection = (sectionId) =>
  API.delete(`/healthcare/healthcare-section/${sectionId}`);

export const apiFetchHealthcareVendors = (vendorId, canViewDirectory) =>
  canViewDirectory
    ? API.get("/healthcare/admin-directory")
    : API.get("/healthcare/healthcare-vendors", {
        params: { vendorId },
      });

export const apiCreateHealthcareProduct = (productData) =>
  API.post(`/healthcare/create-healthcare-product`, productData);

export const apiFetchHealthcareProduct = (productId) =>
  API.get(`/healthcare/healthcare-product/${productId}`);

export const apiUpdateHealthcareProduct = (productId, updatedProductData) =>
  API.put(`/healthcare/healthcare-product/${productId}`, updatedProductData);

export const apiDeleteHealthcareProduct = (productId) =>
  API.delete(`/healthcare/healthcare-product/${productId}`);

export const apiFetchHealthcareProfile = (vendorId) =>
  API.get(`/healthcare/healthcare-profile/${vendorId}`);

export const apiUpdateHealthcareProfile = (vendorId, updatedData) =>
  API.put(`/healthcare/update-healthcare-profile/${vendorId}`, updatedData);

export const apiDeleteHealthcareVendor = (id) =>
  API.delete(`/healthcare/healthcare-vendor/${id}`);

// Healthcare Open Hours Management
export const apiUpdateHealthcareOpenHours = (vendorId, openHoursData) =>
  API.patch(`/healthcare/${vendorId}/open-hours`, openHoursData);

export const apiGetHealthcareOpenStatus = (vendorId) =>
  API.get(`/healthcare/${vendorId}/open-status`);

// Healthcare Outlet Management
export const apiCreateHealthcareOutlet = (healthcareId, outletData) =>
  API.post(`/healthcare/${healthcareId}/outlets`, outletData);

export const apiUpdateHealthcareOutlet = (healthcareId, outletId, updatedData) =>
  API.put(`/healthcare/${healthcareId}/outlets/${outletId}`, updatedData);

export const apiDeleteHealthcareOutlet = (healthcareId, outletId) =>
  API.delete(`/healthcare/${healthcareId}/outlets/${outletId}`);

export const apiToggleHealthcareOutletActivation = (outletId, isActive) =>
  API.put(`/healthcare/${outletId}/toggle-activation`, { isActive });

export const apiUpdateHealthcareOutletMenuItems = (outletId, menuItems) =>
  API.put(`/healthcare/${outletId}/menu-items`, { menuItems });

// Healthcare Outlet Business Hours
export const apiGetHealthcareOutletHours = (outletId) =>
  API.get(`/healthcare/${outletId}/hours`);

export const apiUpdateHealthcareOutletHours = (outletId, hoursData) =>
  API.put(`/healthcare/${outletId}/hours`, hoursData);

// Manage Outlets
export const apiFetchOutlets = (restaurantId) =>
  API.get(`/outlets/restaurant/${restaurantId}`);
export const apiCreateOutlet = (outletData) =>
  API.post(`/outlets/create`, outletData);
export const apiUpdateOutlet = (outletId, updatedData) =>
  API.put(`/outlets/update/${outletId}`, updatedData);
export const apiDeleteOutlet = (outletId) =>
  API.delete(`/outlets/delete/${outletId}`);

// Attach store/outlet to store
export const apiAttachStore = (storeData) =>
  API.post("/outlets/attach-store", storeData);

// Add this with other outlet API functions
export const apiUpdateOutletMenuItems = (outletId, menuItems) =>
  API.put(`/outlets/update/${outletId}/menu-items`, { menuItems });

export const apiToggleOutletActivation = (outletId, isActive) =>
  API.put(`/outlets/toggle-activation/${outletId}`, { isActive });

// Outlet Business Hours
export const apiFetchOutletHours = (outletId) =>
  API.get(`/outlets/${outletId}/hours`);

export const apiUpdateOutletHours = (outletId, hoursData) =>
  API.put(`/outlets/${outletId}/hours`, hoursData);

export const apiDeleteOutletHours = (outletId) =>
  API.delete(`/outlets/${outletId}/hours`);

export const apiFetchAllVendorsForSuperAdmin = () =>
  API.get("/weborders/all-vendors-for-superadmin");

// ── Account Deletion Request Management (Super Admin) ────────────────────────
// Fetch all deletion requests
export const apiFetchDeletionRequests = () =>
  API.get("/user/deletion-requests");

// Process a specific deletion request (deactivate or hard-delete the user)
export const apiProcessDeletionRequest = (requestId, action, adminId) =>
  API.post(`/user/deletion-requests/${requestId}/process`, { action, adminId });

// ── Admin: Fetch menu sections (+ embedded products) for a restaurant ────────
// Reuses the existing sections endpoint — vendorId can be the Restaurants._id
export const apiFetchRestaurantSections = (restaurantId) =>
  API.get(`/restaurants/menu-sections?vendorId=${restaurantId}`);

// Admin: Create a section for a restaurant (uses restaurantId as the user_id
// placeholder — the controller resolves the owner chain internally)
export const apiAdminCreateSection = (restaurantId, sectionData) =>
  API.post(`/restaurants/menu-sections/${restaurantId}`, sectionData);

// Admin: Update section title/description
export const apiAdminUpdateSection = (sectionId, data) =>
  API.patch(`/restaurants/menu-sections/${sectionId}`, data);

// Admin: Delete a section (and its products)
export const apiAdminDeleteSection = (sectionId, reason) =>
  API.delete(`/restaurants/del-restaurant-section/${sectionId}`, {
    data: { reason },
  });

// Admin: Delete a single restaurant product
export const apiAdminDeleteProduct = (productId, reason) =>
  API.delete(`/restaurants/del-restaurant-product/${productId}`, {
    data: { reason },
  });

/* Many sections and dishes in ONE request. Super admin only: the route is
   gated by isSuperUser and the server audits every entity with the reason. */
export const apiAdminBulkDeleteMenu = ({ sectionIds = [], productIds = [], reason }) =>
  API.post(`/restaurants/menu/bulk-delete`, { sectionIds, productIds, reason });

// ── AI Commerce Gateway — isAIShoppable toggle (Super Admin only) ─────────────
// Each vendor type has its own endpoint matching the route file registrations.
export const apiToggleRestaurantAIShoppable = (vendorId, isAIShoppable) =>
  API.patch(`/restaurants/${vendorId}/ai-shoppable`, { isAIShoppable });

export const apiToggleBrandAIShoppable = (vendorId, isAIShoppable) =>
  API.patch(`/api/brands/${vendorId}/ai-shoppable`, { isAIShoppable });

export const apiToggleRetailerAIShoppable = (vendorId, isAIShoppable) =>
  API.patch(`/rw/retailer-wholesaler/${vendorId}/ai-shoppable`, { isAIShoppable });

export const apiToggleHealthcareAIShoppable = (vendorId, isAIShoppable) =>
  API.patch(`/healthcare/healthcare-vendor/${vendorId}/ai-shoppable`, { isAIShoppable });

// ── Vendor sign-in details (SUPER ADMIN only) ────────────────────────────────
// "Credentials" here is the vendor's LOGIN EMAIL plus a single-use, short-lived
// set-password link. A password is never sent and never can be — see
// services/auth/vendorCredentialDelivery.js on the server.
//
// `vendorType` is one of RESTAURANT | RETAILER_WHOLESALER | HEALTHCARE | BRAND.

// Preflight for the confirmation dialog. The destination is resolved on the
// server from the vendor's linked login account, because the directory row
// shows the BUSINESS email, which is often not the address that can sign in.
export const apiGetVendorCredentialTarget = (vendorType, vendorId) =>
  API.get(`/api/vendor-credentials/${vendorType}/${vendorId}`);

// Sends the mail. Takes no body on purpose: nothing in the request can change
// where it goes or where the link points.
export const apiSendVendorCredentials = (vendorType, vendorId) =>
  API.post(`/api/vendor-credentials/${vendorType}/${vendorId}/send`);

// "Set login": for when mail cannot carry the link. The admin sets the login
// email and a password and hands them over by hand. The server stores the hash
// and never sends the password back, so the dialog that typed it is the only
// place it exists (services/auth/vendorLoginSetter.js).
//
// `memberUserId` sets one of the vendor's team members instead of the owner.
// `adminSession` sends the call as the super admin while they are signed in
// as the vendor (the My Team page); see utilities/impersonation.js.
export const apiGetVendorLogin = (vendorType, vendorId, { adminSession = false } = {}) =>
  API.get(`/api/vendor-credentials/${vendorType}/${vendorId}/login`, { adminSession });

export const apiSetVendorLogin = (
  vendorType,
  vendorId,
  { email, password, signOutEverywhere = false, memberUserId = null },
  { adminSession = false } = {}
) =>
  API.post(
    `/api/vendor-credentials/${vendorType}/${vendorId}/set-login`,
    { email, password, signOutEverywhere, ...(memberUserId ? { memberUserId } : {}) },
    { adminSession }
  );

// The signed-in user's own password (any Users account: vendor owner, team
// member, staff). Refused while signed in as a vendor.
export const apiChangeOwnPassword = ({ currentPassword, newPassword }) =>
  API.post(`/user/change-password`, { currentPassword, newPassword });

// ── GA4 Analytics ────────────────────────────────────────────────────────────
export const apiGetAnalyticsOverview = (days = 30) =>
  API.get(`/api/analytics/overview?days=${days}`);

export const apiGetAnalyticsTrend = (days = 30) =>
  API.get(`/api/analytics/trend?days=${days}`);

export const apiGetAnalyticsRealtime = () =>
  API.get(`/api/analytics/realtime`);

export const apiGetVendorShopPath = () =>
  API.get(`/api/analytics/vendor-path`);

export const apiGetVendorRealtimeAnalytics = () =>
  API.get(`/api/analytics/vendor-realtime`);

export const apiGetVendorAnalytics = (path, days = 30) =>
  API.get(`/api/analytics/vendor?path=${encodeURIComponent(path)}&days=${days}`);

// ── Delivery Fee Admin ─────────────────────────────────────────────────────────
export const apiGetDeliveryFeeConfig = () =>
  API.get(`/api/delivery-fee/config`);

// Audit trail for pricing config: "last changed by X on DATE" + capped history.
// Admin-only on the backend.
export const apiGetDeliveryFeeConfigAudit = () =>
  API.get(`/api/delivery-fee/config/audit`);

// Is delivery priced on Google road distance or the straight-line estimate?
// One live Google measurement plus the last 24 hours (staff, settings read).
export const apiGetDistanceHealth = () =>
  API.get(`/api/delivery-fee/distance-health`);

export const apiUpdateDeliveryFeeConfig = (updates) =>
  API.patch(`/api/delivery-fee/config`, updates);

export const apiSetVendorComplexity = (vendorId, vendorType, complexity) =>
  API.patch(`/api/delivery-fee/vendor-complexity`, { vendorId, vendorType, complexity });

// Full "Contract A" quote preview (fee + breakdown + cost/margin + split + signed
// quote token) used by the Fee Transparency UI. Auth'd (admin OR vendor); the
// backend only returns the `cost`/`margin` blocks the caller is allowed to see.
export const apiGetDeliveryFeePreview = ({ vendorId, customerLat, customerLng, mode, complexity, zone } = {}) => {
  const params = new URLSearchParams();
  if (vendorId != null)    params.set("vendorId", vendorId);
  if (customerLat != null) params.set("customerLat", customerLat);
  if (customerLng != null) params.set("customerLng", customerLng);
  if (mode)                params.set("mode", mode);
  if (complexity)          params.set("complexity", complexity);
  if (zone)                params.set("zone", zone);
  return API.get(`/api/delivery-fee/preview?${params.toString()}`);
};

// ── Blog / Insights (Admin) ───────────────────────────────────────────────────
export const apiBlogGetAll = () => API.get(`/api/blog/admin/posts`);
export const apiBlogGetById = (id) => API.get(`/api/blog/admin/posts/${id}`);
export const apiBlogCreate = (data) => API.post(`/api/blog/admin/posts`, data);
export const apiBlogUpdate = (id, data) => API.put(`/api/blog/admin/posts/${id}`, data);
export const apiBlogPublish = (id) => API.patch(`/api/blog/admin/posts/${id}/publish`);
export const apiBlogUnpublish = (id) => API.patch(`/api/blog/admin/posts/${id}/unpublish`);
export const apiBlogDelete = (id) => API.delete(`/api/blog/admin/posts/${id}`);
// Destroy blog-hosted Cloudinary assets that no post references any more.
// The server re-checks references, so a URL still in use is kept.
export const apiBlogDiscardMedia = (urls) =>
  API.post(`/api/blog/admin/media/discard`, { urls: Array.isArray(urls) ? urls : [urls] });

// ── Lusuku Wallet Service API ─────────────────────────────────────────────────
// Separate axios instance pointing directly at the Wallet Service (port 8005).
// The main backend JWT is accepted by the wallet service (shared JWT_SECRET).
// All admin calls use the same bearer token attached by the request interceptor below.

const WALLET_API = axios.create({
  baseURL: process.env.REACT_APP_WALLET_SERVICE_URL || "http://localhost:8005",
});

WALLET_API.interceptors.request.use((req) => {
  const token = getTokenFromLocalStorage();
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// Silent refresh on 401 — mirrors main API interceptor
WALLET_API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const code = error.response?.data?.code;
    if (
      error.response?.status === 401 &&
      (code === "TOKEN_EXPIRED" || code === "INVALID_TOKEN" || code === "NO_TOKEN") &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) throw new Error("No refresh token");
        const { data } = await axios.post(
          `${dashboardApiBaseUrl()}/user/refresh-token`,
          { refreshToken }
        );
        persistRefreshedSession(data);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return WALLET_API(originalRequest);
      } catch {
        sessionExpired('forced');
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ── Admin: Disbursement management ───────────────────────────────────────────
export const apiWalletGetBalance = () =>
  WALLET_API.get("/api/disbursement/admin/balance");

// Treasury snapshot: MTN float vs total obligations (accountant guardrail).
export const apiWalletTreasury = () =>
  WALLET_API.get("/api/disbursement/admin/treasury");

// Live float controls. The reserve used to be an env var frozen at process
// start, so a mis-sized limit needed a redeploy to correct while payouts sat
// blocked. Read is admin/supervisor; write is super-admin only (server-gated).
export const apiWalletGetFloatSettings = () =>
  WALLET_API.get("/api/disbursement/admin/settings/float");

// Treasury thresholds: the UGX amounts that decide when money needs a person
// (reconciliation tolerances, batch approval threshold, manual payout
// tolerance, rider COD limit). Each resolves WalletSetting row > env > shipped
// default in the wallet service. Read is admin/supervisor; write is super-admin
// only (server-gated). Body for the update: any subset of the keys the GET
// returns, each a whole number of UGX, plus an optional reason.
export const apiWalletTreasuryThresholds = () =>
  WALLET_API.get("/api/disbursement/admin/settings/thresholds");
export const apiWalletUpdateTreasuryThresholds = (body) =>
  WALLET_API.put("/api/disbursement/admin/settings/thresholds", body);

// Money IN: customer payments, delivery fees, rider deposits and top-ups, with
// what MTN kept out of each and what landed in the wallet. One service feeds
// this tab and the nightly reconciliation (moneyInService in the wallet).
export const apiWalletMoneyInSummary = (params) =>
  WALLET_API.get("/api/disbursement/admin/money-in/summary", { params });
// Days the MTN wallet moved with nothing in the books to explain it.
export const apiWalletMoneyInUnexplained = (params) =>
  WALLET_API.get("/api/disbursement/admin/money-in/unexplained", { params });
export const apiWalletListMoneyIn = (params) =>
  WALLET_API.get("/api/disbursement/admin/money-in", { params });
// The MTN collection tariff (percent kept out of every MoMo inflow). Read is
// admin/supervisor; write is super-admin only (server-gated), with a reason.
export const apiWalletGetCollectionFee = () =>
  WALLET_API.get("/api/disbursement/admin/settings/collection-fee");
export const apiWalletUpdateCollectionFee = (body) =>
  WALLET_API.put("/api/disbursement/admin/settings/collection-fee", body);
// Body: { watermark?: number|null, enforced?: boolean|null, reason?: string }
// A null value clears the override and falls back to the env/shipped default.
export const apiWalletUpdateFloatSettings = (body) =>
  WALLET_API.put("/api/disbursement/admin/settings/float", body);

// Record the manual treasury cadence in the ledger (idempotent on reference).
export const apiWalletRecordFwSettlement = (body) =>
  WALLET_API.post("/api/disbursement/admin/treasury/fw-settlement", body);
export const apiWalletRecordFloatTopup = (body) =>
  WALLET_API.post("/api/disbursement/admin/treasury/float-topup", body);

export const apiWalletListDisbursements = (params = {}) =>
  WALLET_API.get("/api/disbursement/admin/list", { params });

export const apiWalletGetCircuitBreaker = () =>
  WALLET_API.get("/api/disbursement/admin/circuit-breaker");

export const apiWalletProxyHealth = () =>
  WALLET_API.get("/api/disbursement/admin/proxy-health");

export const apiWalletPollStuck = () =>
  WALLET_API.post("/api/disbursement/admin/poll-stuck");

export const apiWalletGetSummary = () =>
  WALLET_API.get("/api/disbursement/admin/summary");

export const apiWalletGetCommissions = (params = {}) =>
  WALLET_API.get("/api/disbursement/admin/commissions", { params });

export const apiGetVendorCommissionContracts = (params = {}) =>
  API.get("/api/vendor-commissions", { params });

export const apiUpdateVendorCommissionContract = (vendorType, vendorId, body) =>
  API.patch(`/api/vendor-commissions/${vendorType}/${vendorId}`, body);

// VAT registration (WIRE_CONTRACT §17.2). body:
//   { vatRegistered: false }
//   { vatRegistered: true, vatRegistrationNumber: "1000123456" }
export const apiUpdateVendorVatSettings = (vendorType, vendorId, body) =>
  API.patch(`/api/vendor-commissions/${vendorType}/${vendorId}/vat-settings`, body);

// EFRIS identity. Separate endpoint from vat-settings because it answers a
// separate question: not whether the vendor charges VAT, but which taxpayer
// Lusuku files as. A vendor below the VAT threshold has no VAT number and
// still needs a TIN to issue the e-receipt URA requires of it.
export const apiUpdateVendorEfrisSettings = (vendorType, vendorId, body) =>
  API.patch(`/api/vendor-commissions/${vendorType}/${vendorId}/efris-settings`, body);

/* §17.6 — the VENDOR's own view of the same facts. Self-scoped: the server
   resolves which vendor the signed-in account speaks for, so there is no
   vendorId to pass and none to guess. The admin endpoints above administer
   somebody else's vendor and are super-user gated; these are the vendor's own. */
export const apiGetMyVendorTaxSettings = () => API.get("/api/vendor-tax/me");

export const apiUpdateMyVendorTaxSettings = (body) => API.put("/api/vendor-tax/me", body);

export const apiGetMyCommissionStatement = (params = {}) =>
  API.get("/api/vendor-tax/me/statement", { params });

/* The operator's PDF receipt. `orderId` is the ONLY input: the server reads
   every figure from the order. This replaces a hand-built URL that carried the
   customer's name, the totals and the paid status as query parameters on an
   unauthenticated endpoint — which meant the PDF said whatever the caller
   typed. Goes through `API` so the auth token is attached like every other
   call, and returns a Blob for download. */
// The customer receipt as a document: the receipt itself plus the signed
// link to its printable page (controllers/receiptController.js).
export const apiGetOrderReceipt = (orderId) => API.get(`/api/receipts/${orderId}`);
export const apiDownloadOrderReceipt = (orderId) =>
  API.get("/weborders/get_receipt", {
    params: { orderId },
    responseType: "blob",
  });

// ── Vendor: wallet balance & withdrawal ──────────────────────────────────────
export const apiVendorWalletBalance = (vendorId) =>
  WALLET_API.get(`/api/wallet/balance/${vendorId}`);

export const apiVendorWalletTransactions = (vendorId, page = 1, limit = 20) =>
  WALLET_API.get(`/api/wallet/transactions/${vendorId}`, { params: { page, limit } });

export const apiVendorWithdrawalRequest = (vendorId, amount, phoneNumber) =>
  WALLET_API.post("/api/withdrawal/request", { vendorId, amount, phoneNumber });

export const apiVendorWithdrawalHistory = (vendorId, page = 1) =>
  WALLET_API.get(`/api/withdrawal/history/${vendorId}`, { params: { page, limit: 20 } });

export const apiVendorWithdrawalStatus = (withdrawalId) =>
  WALLET_API.get(`/api/withdrawal/status/${withdrawalId}`);

export const apiVendorCalculateFees = (amount) =>
  WALLET_API.get("/api/withdrawal/calculate-fees", { params: { amount } });

export const apiDisbursementStatus = (disbursementId) =>
  WALLET_API.get(`/api/disbursement/status/${disbursementId}`);

export const apiWalletHealth = () =>
  WALLET_API.get("/health");

// ── Phase 4: Batch Payout Management ─────────────────────────────────────────
export const apiWalletListBatches = (params = {}) =>
  WALLET_API.get("/api/disbursement/admin/batches", { params });

export const apiWalletGetBatch = (batchId) =>
  WALLET_API.get(`/api/disbursement/admin/batches/${batchId}`);

export const apiWalletInitiateRiderBatch = (periodStart, periodEnd) =>
  WALLET_API.post("/api/disbursement/admin/batches/initiate-rider", { periodStart, periodEnd });

export const apiWalletInitiatePartnerBatch = (periodStart, periodEnd) =>
  WALLET_API.post("/api/disbursement/admin/batches/initiate-partner", { periodStart, periodEnd });

export const apiWalletApproveBatch = (batchId, note = "") =>
  WALLET_API.post(`/api/disbursement/admin/batches/${batchId}/approve`, { note });

export const apiWalletRejectBatch = (batchId, reason) =>
  WALLET_API.post(`/api/disbursement/admin/batches/${batchId}/reject`, { reason });

export const apiWalletManualPayout = (payload) =>
  WALLET_API.post("/api/disbursement/admin/manual-payout", payload);

// Who a payout to this number reaches: MTN's active check plus the registered
// name, read before a manual payout is committed. Read only.
export const apiWalletLookupPayee = (phone) =>
  WALLET_API.get("/api/disbursement/admin/payee-lookup", { params: { phone } });

export const apiWalletBulkPayoutValidate = (payouts) =>
  WALLET_API.post("/api/disbursement/admin/bulk-payout-validate", { payouts });

export const apiWalletBulkPayout = (payouts) =>
  WALLET_API.post("/api/disbursement/admin/manual-payout", { payouts });

export const apiWalletListManualPayouts = (params = {}) =>
  WALLET_API.get("/api/disbursement/admin/manual-payouts", { params });

export const apiWalletRetryDisbursement = (disbursementId) =>
  WALLET_API.post("/api/disbursement/admin/retry-disbursement", { disbursementId });

// Entity typeahead search — used by Manual Payout UI to find riders/vendors/partners/customers/orders
// type: RIDER | VENDOR | PARTNER | CUSTOMER | ORDER
export const apiEntitySearch = (type, q, limit = 10) =>
  API.get("/api/admin/entity-search", { params: { type, q, limit } });

// Vendor-accessible customer phone lookup — used by In-House Order modal (no admin role required)
export const apiCustomerLookup = (q, limit = 6) =>
  API.get("/api/admin/customer-lookup", { params: { q, limit } });

// ── Phase 5: Reconciliation & Reporting ──────────────────────────────────────
// Default commission per vendor type, used when a vendor has no negotiated rate
// (Lusuku_Wallet_Service/services/walletService.js). Had no screen until the
// Settings menu.
export const apiWalletCommissionRates = () => WALLET_API.get("/api/admin/commission-rates");
export const apiWalletSetCommissionRate = ({ vendorType, commissionPercentage, effectiveFrom }) =>
  WALLET_API.post("/api/admin/commission-rates", { vendorType, commissionPercentage, effectiveFrom });

export const apiWalletFeeBands = () =>
  WALLET_API.get("/api/disbursement/admin/fee-bands");

// Who may be paid for what: payee types, the purposes each allows, defaults
// and labels. The wallet service owns the rule (utilities/payoutPurposes.js).
export const apiWalletPayoutPurposes = () =>
  WALLET_API.get("/api/disbursement/admin/payout-purposes");

export const apiWalletListReconciliation = (params = {}) =>
  WALLET_API.get("/api/disbursement/admin/reconciliation", { params });

export const apiWalletRunReconciliation = (periodStart, periodEnd) =>
  WALLET_API.post("/api/disbursement/admin/reconciliation/run", { periodStart, periodEnd });

// MTN fee corrections: GET is the dry run, POST applies it (rows, ledger, audit).
export const apiWalletFeeCorrectionPlan = () =>
  WALLET_API.get("/api/disbursement/admin/fees/corrections");
export const apiWalletApplyFeeCorrections = (body = {}) =>
  WALLET_API.post("/api/disbursement/admin/fees/corrections", body);

export const apiWalletResolveDiscrepancy = (reportId, discrepancyId, resolutionNote) =>
  WALLET_API.post(`/api/disbursement/admin/reconciliation/${reportId}/resolve/${discrepancyId}`, { resolutionNote });

export const apiWalletMarkReviewed = (reportId, reviewNote = "") =>
  WALLET_API.post(`/api/disbursement/admin/reconciliation/${reportId}/review`, { reviewNote });

// ── Live payout pipeline + report pack (wallet service ≥ 2026-09) ────────────
// Every one of these 404s on an older wallet deploy; callers must fail soft.
const REPORTS_BASE = "/api/disbursement/admin/reports";

// Scheduler snapshot: { serverTime, instanceId, jobs:{queueProcessor,statusPoller,retryHandler,statusSweep}, queue }
export const apiWalletScheduler = () =>
  WALLET_API.get("/api/disbursement/admin/scheduler");

// Reports — JSON
export const apiWalletReportSummary = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/summary`, { params });

export const apiWalletReportRegister = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/register`, { params: { ...params, format: "json" } });

export const apiWalletReportLedger = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/ledger`, { params: { ...params, format: "json" } });

// Reports — CSV as a Blob. A plain <a href> cannot carry the Bearer header the
// interceptor adds, so the file is fetched here and saved by the caller via
// URL.createObjectURL (see saveBlobResponse in WalletDashboard).
export const apiWalletReportRegisterCsv = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/register`, { params: { ...params, format: "csv" }, responseType: "blob", timeout: 120000 });

export const apiWalletReportLedgerCsv = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/ledger`, { params: { ...params, format: "csv" }, responseType: "blob", timeout: 120000 });

// MTN statement reconcile: body { rows:[{financialTransactionId?, externalId?, referenceId?, amount, status?, date?, phone?}], from?, to? }
export const apiWalletMtnStatementReconcile = (body) =>
  WALLET_API.post(`${REPORTS_BASE}/mtn-statement/reconcile`, body, { timeout: 120000 });

// Scheduled (hash-chained) reports
export const apiWalletScheduledReports = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/scheduled`, { params });

export const apiWalletScheduledReport = (reportId) =>
  WALLET_API.get(`${REPORTS_BASE}/scheduled/${encodeURIComponent(reportId)}`);

export const apiWalletGenerateReport = (body) =>
  WALLET_API.post(`${REPORTS_BASE}/scheduled/generate`, body);

// Reports v2 (wallet >= 2026-09-05). All 404 on an older wallet; callers fail soft.
// Settings: { settings, source:{ recipients:"setting"|"env"|"default" }, envLocks:{ reportsDisabled, autoReconcileDisabled } }
export const apiWalletReportSettings = () =>
  WALLET_API.get(`${REPORTS_BASE}/settings`);

// PUT body = partial settings; same response shape as the GET.
export const apiWalletUpdateReportSettings = (body) =>
  WALLET_API.put(`${REPORTS_BASE}/settings`, body);

// Body { recipients?: string[] } -> { posted, recipients, attachments:[names] }; 400 when none resolve, 503 when the wallet cannot post.
export const apiWalletSendTestReportEmail = (body = {}) =>
  WALLET_API.post(`${REPORTS_BASE}/settings/test-email`, body, { timeout: 120000 });

// Overview: { windows:{ today, last7d, last30d }, inFlight, recentFailures, treasury, generatedAt }
export const apiWalletReportOverview = () =>
  WALLET_API.get(`${REPORTS_BASE}/overview`);

// Payments analytics: { period, collections, escrow, refunds, cod, payouts, netFlow, generatedAt }
export const apiWalletPaymentsAnalytics = (params = {}) =>
  WALLET_API.get(`${REPORTS_BASE}/payments`, { params });

// Wallet audit log (routes/admin.js) — { logs, pagination }. Filters: action, entityType, entityId, from, to.
export const apiWalletAuditLogs = (params = {}) =>
  WALLET_API.get("/api/admin/audit-logs", { params });

// ── RBAC — Role-Based Access Control ─────────────────────────────────────────
export const apiRbacGetSummary = () =>
  API.get("/api/rbac/summary");

export const apiRbacGetRoleMatrix = () =>
  API.get("/api/rbac/role-matrix");

export const apiRbacListPermissions = (params) =>
  API.get("/api/rbac/permissions", { params });

export const apiRbacCreatePermission = (data) =>
  API.post("/api/rbac/permissions", data);

export const apiRbacListStaff = (params) =>
  API.get("/api/rbac/staff", { params });

export const apiRbacGetUserDetails = (userId) =>
  API.get(`/api/rbac/staff/${userId}`);

export const apiRbacAssignRole = (userId, role, reason) =>
  API.patch(`/api/rbac/staff/${userId}/role`, { role, reason });

// ── Country scoping (multi_national) ─────────────────────────────────────────
// Staff country-scope management. `fetchStaffUsers` lists staff (country-scoped
// server-side via x-country); `assignStaffCountry` sets a staff member's home
// country / regional scope / super-admin flag (super-admin only on the backend).
export const fetchStaffUsers = (params) =>
  API.get("/api/rbac/staff", { params });

export const assignStaffCountry = (userId, { country, countryScope, isSuperAdmin } = {}) =>
  API.patch(`/api/rbac/staff/${userId}/country`, {
    country,
    countryScope,
    isSuperAdmin,
  });

export const apiRbacBulkAssignRole = (userIds, role, reason) =>
  API.post("/api/rbac/staff/bulk-role", { userIds, role, reason });

export const apiRbacGrantPermissionOverride = (userId, data) =>
  API.post(`/api/rbac/staff/${userId}/permissions`, data);

export const apiRbacReplacePermissionOverrides = (userId, overrides, reason = "") =>
  API.put(`/api/rbac/staff/${userId}/permissions`, { overrides, reason });

export const apiRbacRevokePermissionOverride = (userId, permissionKey) =>
  API.delete(`/api/rbac/staff/${userId}/permissions/${permissionKey}`);

export const apiRbacToggleActivation = (userId, isActive, reason) =>
  API.patch(`/api/rbac/staff/${userId}/activation`, { isActive, reason });

export const apiRbacGetAuditLog = (params) =>
  API.get("/api/rbac/audit-log", { params });

// Effective permissions for the *current* user (incl. per-user overrides).
// Returns { success, data: { user_role, isSuperUser, permissions } } where
// `permissions` is ["*"] for SUPERUSER, else an array of `domain:action` keys.
export const apiRbacMyPermissions = () =>
  API.get("/api/rbac/me/permissions");

// Super-user-only: reset a staff member's password. Body { newPassword }.
export const apiRbacResetStaffPassword = (userId, newPassword) =>
  API.post(`/api/rbac/staff/${userId}/reset-password`, { newPassword });

// Super-user-only: email the staff member a set-password link instead of
// typing a password for them (offboarding-safe — no plaintext shared).
//
// Sends NO link URL. The origin of the emailed link is resolved server-side
// from configuration (services/auth/dashboardOrigin.js) — a client-supplied
// origin would let whoever calls this aim a real Lusuku set-password email,
// carrying a live token, at a host of their choosing. The server ignores the
// field if it is sent anyway, and records the attempt in the audit log.
export const apiRbacSendStaffResetLink = (userId) =>
  API.post(`/api/rbac/staff/${userId}/reset-password`, { sendLink: true });

// Fetch all vendors (restaurants + retailers/wholesalers + healthcare) for SuperAdmin in-house order
export const apiGetAllVendorsForSuperAdmin = () =>
  API.get("/weborders/all-vendors-for-superadmin");

// ── Customer Refunds ──────────────────────────────────────────────────────────
// Initiate a customer refund for a cancelled/disputed order
export const apiInitiateRefund = (payload) =>
  API.post("/api/refunds/initiate", payload);

// Get refund status for a specific order (used by ProcessRefundForm to preview)
export const apiGetOrderRefundStatus = (orderId) =>
  API.get(`/api/refunds/order/${orderId}`);

// ── Audit Trail (Phase 0+) ────────────────────────────────────────────────────
// Vendor activity feed — paginated list of every change made on the vendor's
// account (products, prices, hours, staff, media, admin interventions, etc.).
// Query params accepted by the backend: page, limit, from, to, domain, action,
// actorType, actorUserId, sessionId, entityType, entityId, outletId, vendorId (admin only).
export const apiGetVendorActivity = (params = {}) =>
  API.get("/api/audit/vendor/activity", { params });

// Single activity row — fetches the full before/after snapshot for a row that
// the feed rendered in summary form.
export const apiGetActivityRow = (id) =>
  API.get(`/api/audit/vendor/activity/${id}`);

// Sessions acting on the current vendor's tenant. Used by Phase 4 StaffSessionsView
// but also by Phase 0 "drill-down from session" links in the Activity feed.
export const apiGetVendorSessions = (params = {}) =>
  API.get("/api/audit/vendor/sessions", { params });

// Enum vocabulary for audit filter chips. Cacheable.
export const apiGetAuditMeta = () =>
  API.get("/api/audit/meta");

// Order timeline — enriched status history with per-stage durations and actor
// attribution. Ownership enforced server-side (customer/rider/vendor/partner/admin).
//
// `/weborders`, not `/orders`. ordersRoutes.js — which declares
// `GET /:orderId/timeline` — is mounted at `/weborders` and `/mobile` in
// server.js and NOWHERE else; `/api/orders` is settlementRoutes, a different
// router with no timeline. So this call 404'd for every order on every screen
// that showed the panel, and the screen printed the raw axios message
// ("Request failed with status code 404") where the history should be.
//
// Nothing else in this client used the `/orders/` prefix, which is how one
// wrong word survived: there was no second caller to disagree with it.
export const apiGetOrderTimeline = (orderId) =>
  API.get(`/weborders/${orderId}/timeline`);

// ── Stock Ledger (Phase 3) ────────────────────────────────────────────────────
// Immutable stock movements per product / vendor. Supports filters: productId,
// productType, outletId, type (IN/OUT/ADJUST/RETURN/WASTE/TRANSFER), from, to.
export const apiListStockMovements = (params = {}) =>
  API.get("/api/stock/movements", { params });

// Vendor-owned products/listings available for ledger movements.
export const apiListStockProducts = (params = {}) =>
  API.get("/api/stock/products", { params });

// Compare product.countInStock to last ledger balance → drift report.
export const apiReconcileStockProduct = (productId, productType) =>
  API.get(`/api/stock/reconcile/${productId}`, { params: { productType } });

// Vendor-initiated manual stock movement (restock, waste, correction).
export const apiCreateStockMovement = (payload) =>
  API.post("/api/stock/movements", payload);

// Create first ledger rows from existing catalog stock for products with no history.
export const apiCreateStockOpeningBalances = (payload = {}) =>
  API.post("/api/stock/opening-balances", payload);

// ── Staff Sessions (Phase 4) ─────────────────────────────────────────────────
// Terminate a session (self-logout, tenant-owner revoke, or admin revoke).
export const apiTerminateSession = (sessionId) =>
  API.delete(`/api/audit/vendor/sessions/${sessionId}`);

// ── Reports (Phase 5) ────────────────────────────────────────────────────────
// Activity summary — KPIs + daily series + by-domain breakdown for a period.
export const apiGetAuditSummary = (params = {}) =>
  API.get("/api/audit/vendor/reports/summary", { params });

// Stock KPI report — totals IN/OUT/WASTE + top movers.
export const apiGetAuditStockReport = (params = {}) =>
  API.get("/api/audit/vendor/reports/stock", { params });

// Customer cohorts (retention triangle).
export const apiGetAuditCohorts = (params = {}) =>
  API.get("/api/audit/vendor/reports/cohorts", { params });

// Download the signed PDF statement (returns a Blob).
export const apiGetAuditStatementPdf = (params = {}) =>
  API.get("/api/audit/vendor/reports/statement.pdf", {
    params,
    responseType: "blob",
  });

// ── Data Exports (Phase 6) ───────────────────────────────────────────────────
export const apiGetExportManifest = () =>
  API.get("/api/audit/vendor/exports/manifest");

// Streamed CSV exports — caller should save the Blob response to a file.
export const apiExportAuditActivityCsv = (params = {}) =>
  API.get("/api/audit/vendor/exports/activity.csv", { params, responseType: "blob" });

export const apiExportAuditStockCsv = (params = {}) =>
  API.get("/api/audit/vendor/exports/stock.csv", { params, responseType: "blob" });

export const apiExportAuditSessionsCsv = (params = {}) =>
  API.get("/api/audit/vendor/exports/sessions.csv", { params, responseType: "blob" });

// Any dataset the manifest lists, by the endpoint it gives (orders, order-lines,
// catalogue, team, wallet since 31 Oct 2026). Adding a dataset is a server change.
export const apiExportVendorDatasetCsv = (endpoint, params = {}) =>
  API.get(endpoint, { params, responseType: "blob" });

// ── Feature Entitlements (Phase 8) ───────────────────────────────────────────
// Per-vendor gate for audit-trail modules. See
// wiki/AUDIT_PHASE8_ENTITLEMENTS_PROGRESS.md for the overall design.
//
// - apiGetMyEntitlements() is called once on login + on layout mount to seed
//   EntitlementContext. Returns { role, bypassesEntitlements, vendorId, modules }.
// - apiGetEntitlementMeta() returns the vocabulary (modules + tiers + labels).
//   Cacheable, no auth.
// - Admin endpoints (grant/suspend/revoke/update-tier/provision-defaults) are
//   gated server-side to SUPERUSER / ADMIN / SUPERVISOR roles.

export const apiGetMyEntitlements = () => API.get("/api/entitlements/me");

export const apiGetEntitlementMeta = () => API.get("/api/entitlements/meta");

export const apiListAllEntitlements = (params = {}) =>
  API.get("/api/entitlements", { params });

export const apiGetVendorEntitlements = (vendorId) =>
  API.get(`/api/entitlements/vendor/${vendorId}`);

export const apiGrantEntitlement = (vendorId, payload) =>
  API.post(`/api/entitlements/vendor/${vendorId}/grant`, payload);

export const apiSuspendEntitlement = (vendorId, payload) =>
  API.post(`/api/entitlements/vendor/${vendorId}/suspend`, payload);

export const apiRevokeEntitlement = (vendorId, payload) =>
  API.post(`/api/entitlements/vendor/${vendorId}/revoke`, payload);

export const apiUpdateEntitlementTier = (vendorId, payload) =>
  API.post(`/api/entitlements/vendor/${vendorId}/update-tier`, payload);

export const apiProvisionEntitlementDefaults = (vendorId, payload = {}) =>
  API.post(`/api/entitlements/vendor/${vendorId}/provision-defaults`, payload);

// ── URA product classification (platform admin only) ────────────────────────
// The Lusuku-side mapping of the four catalogues onto URA's standardised
// product list. Every one of these answers 403 for a vendor session; the field
// is not the vendor's to set. See controllers/fiscalClassificationController.js.
export const apiFiscalClassificationMeta = () =>
  API.get("/api/fiscal-classification/meta");

export const apiFiscalClassificationCoverage = () =>
  API.get("/api/fiscal-classification/coverage");

export const apiListProductsForClassification = (params = {}) =>
  API.get("/api/fiscal-classification/products", { params });

export const apiSetProductClassification = (vendorType, productId, payload) =>
  API.patch(`/api/fiscal-classification/products/${vendorType}/${productId}`, payload);

export const apiBulkSetProductClassification = (payload) =>
  API.post("/api/fiscal-classification/products/bulk", payload);

// Map a Lusuku category or unit onto URA's list from the classification page,
// the "Map category" / "Map unit" step on a product row. Every product that
// carries the ref and no code of its own then inherits it. The server takes
// URA's name and rate from its own copy of the list; only the code is sent.
export const apiSetCategoryUraCode = (categoryId, goodsCategoryId) =>
  API.patch(`/api/fiscal-classification/categories/${categoryId}`, { goodsCategoryId });

export const apiSetUnitUraCode = (unitId, unitOfMeasure) =>
  API.patch(`/api/fiscal-classification/units/${unitId}`, { unitOfMeasure });

// The calling VENDOR's own coverage, same shape as /coverage. Self-scoped the
// way /api/vendor-tax/me is: the server works out whose catalogue it is, so
// there is no vendorId to pass. Read-only on purpose; a vendor cannot set a
// URA code and the panel that shows this says who can.
export const apiMyFiscalClassificationCoverage = () =>
  API.get("/api/fiscal-classification/my-coverage");

// ── Fiscal documents (the Tax menu) ─────────────────────────────────────────
// Every invoice and credit note the fiscal submitter has raised, in both URA
// environments. Each row carries its own `mode` and the payload carries the
// submitter's `environment`, because a sandbox FDN looks exactly like a live
// one and the screen must print the environment beside every number.
//   params: kind, status, mode, vendorType, vendorId, from, to, q, page, limit
export const apiListFiscalDocuments = (params = {}) =>
  API.get("/api/fiscal-documents", { params });

// A signed link to the printable e-invoice or credit note (URA's sections A to
// F, the FDN, verification code and QR), for the document dialog. 409 { code,
// message } when the row is not a document URA issued: AWAITING_APPROVAL or
// NOT_ISSUED.
export const apiGetFiscalDocumentLink = (id) =>
  API.get(`/api/fiscal-documents/${id}/document`);

// Credit notes URA has accepted as applications and not yet approved. The
// application reference on each row is NOT a fiscal document number.
export const apiFiscalCreditNotesAwaitingApproval = (params = {}) =>
  API.get("/api/fiscal-credit-notes/awaiting-approval", { params });

// Ask URA to cancel an ISSUED credit note (T114, by its FDN), body
// { reason, reasonCode }. 202 with the updated row, 409 { code, message } when
// the credit note is not cancellable (still awaiting approval, not issued).
// URA's answer comes back on cancellation.outcome and cancellation.uraDetail.
export const apiCancelFiscalCreditNote = (id, body) =>
  API.post(`/api/fiscal-credit-notes/${id}/cancel`, body);

// Raise a credit note against an ISSUED commission invoice (tax:manage).
// { invoiceId, reason, gross? }: the whole invoice when gross is omitted.
// Answers 201 with the new row in the fiscal documents row shape; the
// submitter lodges it with URA as an application, so it starts PENDING and
// shows AWAITING_APPROVAL once URA has accepted it. Refusals carry a code:
// INVOICE_NOT_ISSUED, ALREADY_CREDITED, AMOUNT_INVALID, REASON_REQUIRED.
export const apiRaiseFiscalCreditNote = (body) =>
  API.post("/api/fiscal-credit-notes", body);

// The EFRIS readiness report, as sections of key/value pairs. Masked values
// arrive masked and are shown as given.
export const apiFiscalHealth = () => API.get("/api/fiscal-health");

// ── URA reference codes for the catalogue mapping pickers ───────────────────
// Search is SERVER-SIDE. The commodity list is about 148,000 leaf codes, so
// the picker sends what the admin typed and never asks for the whole list.
// Envelope: { items, present, total } (see utilities/uraMapping.js for the
// tolerant reader). `present: false` means the dictionaries are not loaded on
// that server; the picker then shows a notice and a manual code field.
export const fetchUraCommodityPresets = (q, limit = 50) =>
  API.get("/category/ura-presets", { params: { q, limit } });

export const fetchUraUnitPresets = (q) =>
  API.get("/unit/ura-presets", { params: { q } });

// ── Seed Feature (community vendor onboarding) ────────────────────────────────
// Ops-only endpoints for reviewing, approving, rejecting, or requesting more
// info on seeder-submitted vendors. Seeder-facing endpoints live on the mobile
// app; the dashboard only owns the review UI.
export const apiSeedListForReview = (params = {}) =>
  API.get("/api/v1/seeds/ops/seeds", { params });

export const apiSeedGetForReview = (id) =>
  API.get(`/api/v1/seeds/ops/seeds/${id}`);

export const apiSeedApprove = (id, payload) =>
  API.patch(`/api/v1/seeds/ops/seeds/${id}/approve`, payload);

export const apiSeedReject = (id, payload) =>
  API.patch(`/api/v1/seeds/ops/seeds/${id}/reject`, payload);

export const apiSeedRequestInfo = (id, payload) =>
  API.patch(`/api/v1/seeds/ops/seeds/${id}/request-info`, payload);

export const apiSeedSetProductQc = (id, pid, payload) =>
  API.patch(`/api/v1/seeds/ops/seeds/${id}/products/${pid}/qc`, payload);

// ── Seed — catalog change requests (edits to already-active vendor catalogs)
export const apiSeedListCatalogChanges = (params = {}) =>
  API.get("/api/v1/seeds/ops/catalog-changes", { params });

export const apiSeedGetCatalogChange = (id) =>
  API.get(`/api/v1/seeds/ops/catalog-changes/${id}`);

export const apiSeedApproveCatalogChange = (id, payload) =>
  API.patch(`/api/v1/seeds/ops/catalog-changes/${id}/approve`, payload);

export const apiSeedRejectCatalogChange = (id, payload) =>
  API.patch(`/api/v1/seeds/ops/catalog-changes/${id}/reject`, payload);

// ── Seed — credit config singleton (reward amounts + redemption caps)
export const apiSeedGetConfig = () =>
  API.get("/api/v1/seeds/ops/config");

export const apiSeedUpdateConfig = (payload) =>
  API.patch("/api/v1/seeds/ops/config", payload);

// ── POS Cashier PIN management ────────────────────────────────────────────────
// Vendors set 4-digit PINs for their cashiers (owner self + seller helpers);
// platform admins can reset PINs for any vendor.
//
// - apiPosListCashiers()           → owner's own team + hasCustomPin flags
// - apiPosListCashiers(vendorId)   → (admin) a specific vendor's team
// - apiPosSetCashierPin()          → vendor sets own cashier's PIN
// - apiPosAdminSetCashierPin()     → admin override for any vendor
export const apiPosListCashiers = (vendorUserId) =>
  API.get("/api/pos/cashiers", {
    params: vendorUserId ? { vendorUserId } : {},
  });

export const apiPosSetCashierPin = (cashierId, newPin) =>
  API.put("/api/pos/cashier-pin", { cashierId, newPin });

export const apiPosAdminSetCashierPin = (vendorUserId, cashierId, newPin) =>
  API.put("/api/pos/admin/cashier-pin", { vendorUserId, cashierId, newPin });

// POS terminal fleet management — super-admin only.
export const apiPosFleetMetadata = () =>
  API.get("/api/pos/admin/devices/metadata");

export const apiPosListTerminals = (params = {}) =>
  API.get("/api/pos/admin/devices", { params });

export const apiPosGetTerminal = (terminalId) =>
  API.get(`/api/pos/admin/devices/${encodeURIComponent(terminalId)}`);

export const apiPosGetVendorOutlets = (vendorUserId) =>
  API.get(
    `/api/pos/admin/vendors/${encodeURIComponent(vendorUserId)}/outlets`
  );

export const apiPosUpdateTerminal = (terminalId, payload) =>
  API.patch(`/api/pos/admin/devices/${encodeURIComponent(terminalId)}`, payload);

export const apiPosQueueTerminalCommand = (
  terminalId,
  type,
  payload = {},
  options = {}
) =>
  API.post(`/api/pos/admin/devices/${encodeURIComponent(terminalId)}/commands`, {
    type,
    payload,
    ...options,
  });

// Cancel a queued command. Succeeds only while it is PENDING or DELIVERED; a
// DELIVERED command may already have run on the device and the response says so.
export const apiPosCancelTerminalCommand = (terminalId, commandId) =>
  API.delete(
    `/api/pos/admin/devices/${encodeURIComponent(
      terminalId
    )}/commands/${encodeURIComponent(commandId)}`
  );

// Rotate — or revoke — a terminal's opaque `posd_` credential. Rotation returns
// the new plaintext token EXACTLY ONCE; nothing on the server can recover it
// afterwards. `{ revoke: true }` burns the credential without issuing a usable
// replacement and suspends the terminal — the correct answer to a leak.
export const apiPosRotateTerminalCredential = (terminalId, data = {}) =>
  API.post(
    `/api/pos/admin/devices/${encodeURIComponent(terminalId)}/rotate-credential`,
    data
  );

// ── POS enrollment codes ─────────────────────────────────────────────────────
// Provision a terminal without typing a vendor password into the hardware. The
// admin mints a short code carrying the whole assignment; the operator types it
// on the terminal's Set Up screen. Codes are readable until they expire so ops
// can re-read one to a field agent. Behind POS_ENROLLMENT_CODES_ENABLED — these
// return 404 while the flag is off.
export const apiPosCreateEnrollmentCode = (payload) =>
  API.post("/api/pos/admin/enrollment-codes", payload);

export const apiPosListEnrollmentCodes = (params = {}) =>
  API.get("/api/pos/admin/enrollment-codes", { params });

export const apiPosRevokeEnrollmentCode = (id, payload = {}) =>
  API.post(
    `/api/pos/admin/enrollment-codes/${encodeURIComponent(id)}/revoke`,
    payload
  );

// ── Vendor media billing / image quota ───────────────────────────────────────
export const apiGetMyVendorMediaUsage = (params = {}) =>
  API.get("/api/vendor-media/usage/me", { params });

export const apiCreateVendorMediaPurchase = (payload) =>
  API.post("/api/vendor-media/purchase", payload);

export const apiCheckVendorMediaPurchasePayment = (purchaseId) =>
  API.post(`/api/vendor-media/purchase/${purchaseId}/check-payment`);

export const apiGetVendorMediaAdminRows = () =>
  API.get("/api/vendor-media/admin/vendors");

export const apiGetVendorMediaPurchases = (params = {}) =>
  API.get("/api/vendor-media/admin/purchases", { params });

export const apiGetVendorMediaAudit = (params = {}) =>
  API.get("/api/vendor-media/admin/audit", { params });

export const apiApproveVendorMediaPurchase = (purchaseId) =>
  API.post(`/api/vendor-media/admin/purchase/${purchaseId}/approve`);

export const apiRejectVendorMediaPurchase = (purchaseId, reason) =>
  API.post(`/api/vendor-media/admin/purchase/${purchaseId}/reject`, { reason });

// Admin-editable media billing knobs (free-image allowance + enforcement mode).
export const apiGetVendorMediaSettings = () =>
  API.get("/api/vendor-media/admin/settings");

export const apiUpdateVendorMediaSettings = (payload) =>
  API.put("/api/vendor-media/admin/settings", payload);

export const apiRemoveVendorProfileMedia = (vendorType, vendorId, mediaRole) =>
  API.delete(
    `/api/vendor-media/profile/${encodeURIComponent(vendorType)}/${encodeURIComponent(
      vendorId
    )}/${encodeURIComponent(mediaRole)}`
  );

// ── Reusable platform charges (feature billing) ──────────────────────────────
// POST /api/charges { purpose, phone, idempotencyKey?, ...purposeParams } —
// the amount is resolved SERVER-side by the purpose registry.
export const apiCreatePlatformCharge = (payload) =>
  API.post("/api/charges", payload);

export const apiCheckPlatformCharge = (chargeId) =>
  API.post(`/api/charges/${chargeId}/check`);

export const apiListPlatformCharges = (params = {}) =>
  API.get("/api/charges", { params });

// ── Unified per-item catalog activation ──────────────────────────────────────
// source ∈ inhouse | retail | brand | restaurant | healthcare
export const apiSetCatalogItemStatus = (source, productId, enabled) =>
  API.patch(`/api/catalog/items/${source}/${productId}/status`, { enabled });
// Many at once (QC after an import: new products start deactivated). Same
// permission rule per product; ones the caller may not change are reported,
// not fatal. Returns data { enabled, changed[], unchanged[], forbidden[], notFound[] }.
export const apiSetCatalogItemStatusBulk = (source, productIds, enabled) =>
  API.patch(`/api/catalog/items/${source}/status`, { productIds, enabled });
// Sold out for the day (restaurant dishes with a daily plate limit).
// body: { dailyLimit?: number|null, todaysLimit?: number|null, soldOutToday?: boolean }
export const apiSetRestaurantDailyAvailability = (productId, body) =>
  API.patch(`/restaurants/menu-products/${productId}/daily-availability`, body);

// ── Ticketing vertical (events, seats, secure QR, organizer commission) ───────
export const apiTicketingConfig = () => API.get("/api/ticketing/config");
export const apiTicketingGetSettings = () => API.get("/api/ticketing/settings");
export const apiTicketingUpdateSettings = (payload) => API.put("/api/ticketing/settings", payload);
export const apiTicketingMyOrganizers = () => API.get("/api/ticketing/organizers/mine");
export const apiTicketingCreateOrganizer = (b) => API.post("/api/ticketing/organizers", b);
export const apiTicketingGetOnboarding = (organizerId) =>
  API.get(`/api/ticketing/organizers/${organizerId}/onboarding`);
export const apiTicketingSubmitOnboarding = (organizerId, body) =>
  API.post(`/api/ticketing/organizers/${organizerId}/onboarding/submit`, body);
export const apiTicketingReviewOnboarding = (organizerId, body) =>
  API.post(`/api/ticketing/organizers/${organizerId}/onboarding/review`, body);
export const apiTicketingVenues = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/venues`);
export const apiTicketingCreateVenue = (b) => API.post("/api/ticketing/venues", b);
export const apiTicketingUpdateVenue = (id, b) => API.put(`/api/ticketing/venues/${id}`, b);
export const apiTicketingDeleteVenue = (id) => API.delete(`/api/ticketing/venues/${id}`);
export const apiTicketingSeatMaps = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/seatmaps`);
export const apiTicketingCreateSeatMap = (b) => API.post("/api/ticketing/seatmaps", b);
export const apiTicketingUpdateSeatMap = (id, b) => API.put(`/api/ticketing/seatmaps/${id}`, b);
export const apiTicketingDeleteSeatMap = (id) => API.delete(`/api/ticketing/seatmaps/${id}`);
export const apiTicketingEvents = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/events`);
export const apiTicketingCreateEvent = (b) => API.post("/api/ticketing/events", b);
export const apiTicketingShowings = (eventId) => API.get(`/api/ticketing/events/${eventId}/showings`);
export const apiTicketingCreateShowing = (b) => API.post("/api/ticketing/showings", b);
export const apiTicketingPublishShowing = (id) => API.post(`/api/ticketing/showings/${id}/publish`);
// Inverse of publish — PUBLISHED back to DRAFT, no refunds (that is Cancel).
export const apiTicketingUnpublishShowing = (id) => API.post(`/api/ticketing/showings/${id}/unpublish`);
export const apiTicketingUpdateShowing = (id, body) => API.put(`/api/ticketing/showings/${id}`, body);
export const apiTicketingSales = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/sales`);
export const apiTicketingRedeem = (token, gate) => API.post("/api/ticketing/redeem", { token, ...(gate ? { gate } : {}) });
export const apiTicketingProvisionAccount = (organizerId, body) =>
  API.post(`/api/ticketing/organizers/${organizerId}/account`, body);
export const apiTicketingListStaff = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/staff`);
export const apiTicketingAddStaff = (organizerId, body) => API.post(`/api/ticketing/organizers/${organizerId}/staff`, body);
export const apiTicketingRemoveStaff = (organizerId, userId) => API.delete(`/api/ticketing/organizers/${organizerId}/staff/${userId}`);
// Delivery status for one sent email, by the capability refId returned at
// send time (invites, credential emails). Used by EmailDeliveryBadge.
export const apiMailDeliveryStatus = (refId) => API.get(`/api/mail/delivery/${refId}`);
export const apiTicketingUpdateOrganizer = (id, body) =>
  API.put(`/api/ticketing/organizers/${id}`, body);
export const apiTicketingUpdateEvent = (id, body) =>
  API.put(`/api/ticketing/events/${id}`, body);
export const apiTicketingOverview = () => API.get("/api/ticketing/overview");
export const apiTicketingResetAccount = (organizerId) =>
  API.post(`/api/ticketing/organizers/${organizerId}/account/reset`);
export const apiTicketingDeleteShowing = (id) => API.delete(`/api/ticketing/showings/${id}`);
export const apiTicketingListRefunds = (organizerId) => API.get(`/api/ticketing/organizers/${organizerId}/refunds`);
export const apiTicketingDecideRefund = (id, body) => API.post(`/api/ticketing/refunds/${id}/decide`, body);
export const apiTicketingMarkRefundProcessed = (id, body) => API.post(`/api/ticketing/refunds/${id}/mark-processed`, body);
export const apiTicketingCancelShowing = (id) => API.post(`/api/ticketing/showings/${id}/cancel`);
export const apiTicketingDeleteEvent = (id) => API.delete(`/api/ticketing/events/${id}`);
export const apiTicketingDeleteOrganizer = (id) => API.delete(`/api/ticketing/organizers/${id}`);

// ── Ticketing hold policy (how long a seat stays reserved) ───────────────────
// scope = { organizerId, showingId, eventId, salesChannel } — all optional;
// with none of them you get the platform-level effective policy.
export const apiTicketingHoldPolicy = (scope = {}) =>
  API.get("/api/ticketing/hold-policy", { params: scope });
export const apiTicketingHoldPolicyOverrides = () =>
  API.get("/api/ticketing/hold-policy/organizers");
export const apiTicketingUpdatePlatformHoldPolicy = (body) =>
  API.put("/api/ticketing/hold-policy/platform", body);
export const apiTicketingUpdateOrganizerHoldPolicy = (organizerId, body) =>
  API.put(`/api/ticketing/hold-policy/organizers/${organizerId}`, body);
export const apiTicketingUpdateShowingHoldPolicy = (showingId, body) =>
  API.put(`/api/ticketing/hold-policy/showings/${showingId}`, body);

// The configured instance, for action creators that need it directly.
export { API as dashboardApi };

// ── Kitchen Display System ───────────────────────────────────────────────────
// Boards, ticket actions, station routing and the kitchen report. Every call
// rides the configured API instance above, so it carries the Bearer token, the
// active-country header and the silent-refresh interceptor. A bare axios call
// here would 401 for a perfectly signed-in operator.
const kitchenQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const apiKitchenMeta = () => API.get("/api/kitchen/meta");

/**
 * The kitchens this account may open, with their outlets and live load.
 * A vendor gets exactly one; Lusuku staff get the list they choose from.
 */
export const apiKitchenList = (params = {}) =>
  API.get(`/api/kitchen/kitchens${kitchenQuery(params)}`);

export const apiKitchenBoard = (params = {}) =>
  API.get(`/api/kitchen/board${kitchenQuery(params)}`);

export const apiKitchenOpsBoard = (params = {}) =>
  API.get(`/api/kitchen/ops/board${kitchenQuery(params)}`);

export const apiKitchenTicket = (ticketId) =>
  API.get(`/api/kitchen/tickets/${ticketId}`);

export const apiKitchenVerifyTicket = (ticketId) =>
  API.get(`/api/kitchen/tickets/${ticketId}/verify`);

export const apiKitchenBumpTicket = (ticketId, body = {}) =>
  API.post(`/api/kitchen/tickets/${ticketId}/bump`, body);

export const apiKitchenTransitionTicket = (ticketId, body) =>
  API.post(`/api/kitchen/tickets/${ticketId}/transition`, body);

export const apiKitchenRecallTicket = (ticketId, body) =>
  API.post(`/api/kitchen/tickets/${ticketId}/recall`, body);

export const apiKitchenPrioritiseTicket = (ticketId, body) =>
  API.post(`/api/kitchen/tickets/${ticketId}/priority`, body);

export const apiKitchenTransitionItem = (ticketId, body) =>
  API.post(`/api/kitchen/tickets/${ticketId}/items`, body);

export const apiKitchenRerouteItem = (ticketId, body) =>
  API.post(`/api/kitchen/tickets/${ticketId}/items/station`, body);

export const apiKitchenStations = (params = {}) =>
  API.get(`/api/kitchen/stations${kitchenQuery(params)}`);

export const apiKitchenCreateStation = (body, params = {}) =>
  API.post(`/api/kitchen/stations${kitchenQuery(params)}`, body);

export const apiKitchenUpdateStation = (stationId, body, params = {}) =>
  API.put(`/api/kitchen/stations/${stationId}${kitchenQuery(params)}`, body);

export const apiKitchenDeleteStation = (stationId, params = {}) =>
  API.delete(`/api/kitchen/stations/${stationId}${kitchenQuery(params)}`);

export const apiKitchenSeedStations = (body = {}, params = {}) =>
  API.post(`/api/kitchen/stations/seed${kitchenQuery(params)}`, body);

export const apiKitchenPreviewRouting = (body = {}, params = {}) =>
  API.post(`/api/kitchen/stations/preview${kitchenQuery(params)}`, body);

export const apiKitchenAnalytics = (params = {}) =>
  API.get(`/api/kitchen/analytics${kitchenQuery(params)}`);

export const apiKitchenExportUrl = (params = {}) =>
  `${dashboardApiBaseUrl()}/api/kitchen/analytics/export${kitchenQuery(params)}`;

export const apiKitchenExport = (params = {}) =>
  API.get(`/api/kitchen/analytics/export${kitchenQuery(params)}`, {
    responseType: "blob",
  });

export const apiKitchenBackfill = (body = {}) =>
  API.post("/api/kitchen/backfill", body);

export const apiGetMerchantPaymentClaims = orderId => API.get(`/weborders/${orderId}/payment/recovery/merchant-claims`);
export const apiSubmitMerchantPaymentClaim = (orderId, body) => API.post(`/weborders/${orderId}/payment/recovery/merchant-claims`, body);
